import ElakayImage from '../../../components/images/ElakayImage';
import { getOptimizedImage } from '../../../components/images/resolveImage';
// =============================================================================
//  PageRenderer.jsx — Composant React autonome (mis à jour)
//  Rend une page sauvegardée du PageBuilder en pleine page, avec :
//    - le doc complet sauvegardé ({ meta, pages, currentPageId, pageMode, ... })
//    - les données du pageMode (infoSource pour store, sections pour contentPage…)
//  Applique automatiquement les data-bindings (répétition, bindKey, fieldMap)
//  comme le fait le builder, pour que le rendu corresponde exactement à l'aperçu.
//
//  Utilisation :
//     import PageRenderer from "./PageRenderer";
//     <PageRenderer doc={savedPage.doc} pageData={infoSourceFromAPI} />
//
//  Props :
//    - doc       : la structure sauvegardée { meta, currentPageId, pageMode, pages: [...] }
//    - pageData  : les données externes selon le pageMode :
//                    * "store"       → infoSource (products, owner, …)
//                    * "contentPage" → { sections, pageTitle, … }
//                    * "static"      → null (non utilisé)
//                  La clé sous laquelle ces données sont accessibles dans les
//                  bindings se déduit de doc.pageMode.
//    - pageId    : id de la page à afficher (par défaut : doc.currentPageId)
//    - device    : "desktop" | "mobile" (par défaut : desktop)
//    - onNavigate: callback optionnel quand un lien interne change de page
//    - onSign    : callback appelé quand le visiteur signe un DocViewer interactif.
//                  Reçoit { type: "draw"|"text", image|name+date, docName, signedAt }.
//                  La sauvegarde de la signature est de votre ressort (POST API).
//
//  Données documentaires (DocViewer) :
//    Un nœud de type "docViewer" peut recevoir son contenu de trois façons :
//      1) Via un binding (bindKey absolu pointant vers un OBJET dans pageData) :
//         ex. node.bindKey = "contentPage.contract"
//         où pageData.contract = { docName, docUrl, docHtml }
//         → cas typique avec une API qui sert le contenu du document.
//      2) Directement sur le nœud (node.docName / docUrl / docHtml).
//      3) Aucun : un placeholder « Aucun document attaché » s'affiche.
//
//  Signature requise (requireSign) :
//    Si node.requireSign est true, le contenu du document est flouté et
//    non-interactif tant que le visiteur n'a pas signé.
//    En rendu utilisateur final: vue + signature seulement.
// =============================================================================

import React, { useState, useEffect, useRef, useContext, createContext, useMemo, useCallback } from "react";
import { withToggleBackgroundColor } from "./pageRendererStyleUtils";
import { createPortal } from "react-dom";
import { withProductShop } from "../../shops/productShop";
import ElakaySwiperBlock from "../../dynamicSwipers/ElakaySwiperBlock";
import { MemoryRouter, useInRouterContext } from "react-router-dom";
import { FileText, PenLine, Check } from "lucide-react";
import Vote from "../commentaire/vote";
import SwiperPubCategoryProdMatrix from "../../slider/swiperUserProd/swiperPubCategoryProdMatrix";
import SwiperCategoriesMatrix from "../../slider/swiperUserProd/swiperCategoriesMatrix";
import ProductsPrice from "../productsPrice";
import SwiperPub1500x400 from "../../slider/swiperPub1500x400";
import ShopHeroSlider from "../../dynamicSwipers/ShopHeroSlider";
import ProductGridShop from "../ProductGridShop";
import ShopProductGridBlock, { ShopProductGridSwitch } from "./ShopProductGridBlock";
import { DEFAULT_SHOP_SORT, normalizeShopSort, sortShopProductsLocally } from "./shopProductGridOptions";
import Card from "../Card";
import ShowImage from "../ShowImage";
import ButtonPagination from "../ButtonPagination";
import HeaderShopSeller from "../../Layout/headerShopSeller";
import { laguangeElakay } from "../dataElakay";
import elakayEmptyBox from "../../Layout/icon/elakayEmptyBox.png";
import { getLakayUser } from "../../auth/apiProduct";
import { addFollowUserShop, addItem, getCart as getCartApi, isAuthenticated } from "../../auth";

// ── Live shop CSS — ensures styles load on every page using PageRenderer ───────
import "../../slider/swiperUserProd/swiperPubCategoryProdMatrix.css";
import "../../slider/swiperUserProd/SwiperCategoriesMatrix.css";
import "../../slider/styles.css";
import "../Card.css";
import "../ProductGridPage.css";
import "../ProductsPrice.css";
import { elakayAutoText } from "../dataElakay";
import { getProductPrimaryImage } from "../../auth/elakay-utils";
import { buildCartItemFromProduct } from "../../../utils/cartItem";
import { PAGE_HTML_SANDBOX, safePageHref, safePageResourceUrl, securePageHtmlDocument } from "./pageRendererSecurity";
import { getFreeLayout, getPageLayoutMode } from "./pageBuilderFreeLayout";
import { toFileUrl } from "../../../utils/fileUrl";

const DeviceContext = createContext("desktop");

function ViewportModal({ children }) {
  if (typeof document === "undefined") return children;
  return createPortal(children, document.body);
}

// Renders a page's manually-edited HTML override ("Edit HTML Code", see
// PageHtmlSourceModal.jsx) instead of walking `doc`. Deliberately a
// SANDBOXED iframe, not `dangerouslySetInnerHTML` straight into this app's
// DOM: this HTML can be authored by any Family/owner account, is served to
// real visitors on the public storefront, and must never be able to read
// this app's cookies/localStorage/session even if it contains a <script> --
// omitting "allow-same-origin" keeps the frame at a unique opaque origin
// regardless of "allow-scripts". (No auto-height measurement is attempted
// for the same reason: without allow-same-origin, the parent cannot read
// contentWindow.document cross-origin -- a generous fixed height plus
// CSS resize is the safe trade-off.)
// `pageData`/`sourceKey` (the SAME data the normal JSON-tree render path
// below already receives) re-hydrate the saved HTML's bound nodes against
// the CURRENT database/language values -- see hydrateManualHtmlWithPageData's
// own header comment for the full "HTML manuel + PageData" mission rationale.
// Recomputed via useMemo on [html, pageData, sourceKey] -- deliberately NOT
// memoized on `html` alone (task spec §11's own explicit warning: that would
// leave the iframe stuck on whichever language/data was live the first time
// it rendered).
function ManualHtmlPageFrame({ html, pageData = null, sourceKey = null }) {
  const hydratedHtml = useMemo(
    () => hydrateManualHtmlWithPageData(html, pageData, sourceKey),
    [html, pageData, sourceKey]
  );
  const securedHtml = useMemo(() => securePageHtmlDocument(hydratedHtml), [hydratedHtml]);


  return (
    <iframe
      title={elakayAutoText("h_035d2b64eedc4a", "Page HTML")}
      srcDoc={securedHtml}
      sandbox={PAGE_HTML_SANDBOX}
      referrerPolicy="no-referrer"
      style={{ width: "100%", minHeight: "100vh", border: "none", display: "block" }}
    />
  );
}

// Breakpoints device grid — doit correspondre à DEVICE_GRID_BREAKPOINTS dans PageBuilder
// Breakpoints identiques à DEVICE_GRID_BREAKPOINTS dans PageBuilder.jsx
// IMPORTANT : "mobile" doit être présent sinon { device:"mobile" } génère une règle
// sans media query (s'applique à tout) et se fait écraser par "tablet".
const DG_QUERIES = {
  all:     null,
  mobile:  "@media (max-width: 480px)",
  tablet:  "@media (min-width: 481px) and (max-width: 768px)",
  laptop:  "@media (min-width: 769px) and (max-width: 1200px)",
  desktop: "@media (min-width: 1201px)",
};

// Génère le CSS responsive pour une grille à partir de options.deviceGrid.
// Les règles sans media query (device:"all") sont émises EN PREMIER pour que
// les règles avec media query les écrasent correctement (cascade CSS).
function buildDeviceGridCSS(className, deviceGrid) {
  if (!deviceGrid?.length) return "";
  const sorted = [...deviceGrid].sort((a, b) => {
    const order = { all: 0, mobile: 1, tablet: 2, laptop: 3, desktop: 4 };
    return (order[a.device] ?? 5) - (order[b.device] ?? 5);
  });
  return sorted.map(({ device, columns, gap }) => {
    const cols = Math.max(1, Number(columns) || 3);
    const gapVal = gap || "16px";
    const rule = `.${className} { display: grid !important; grid-template-columns: repeat(${cols}, minmax(0,1fr)) !important; gap: ${gapVal} !important; }`;
    const q = DG_QUERIES[device] ?? null;
    return q ? `${q} { ${rule} }` : rule;
  }).join("\n");
}
const DataContext = createContext({
  data: null,
  sourceKey: null,
  infoSource: null,
  token: null,
  storeActions: null,
  storeState: null,
  followState: null,
  toggleGroupStyles: null,
});

function effStyle(node, device) {
  if (device === "mobile" && node.mobileStyle) return { ...(node.style || {}), ...node.mobileStyle };
  return node.style || {};
}

function parseConsoleCssToReactStyle(cssText = "") {
  if (!cssText || typeof cssText !== "string") return {};
  return cssText
    .split(";")
    .map((line) => line.trim())
    .filter(Boolean)
    .reduce((acc, line) => {
      const colonIndex = line.indexOf(":");
      if (colonIndex <= 0) return acc;
      const rawKey = line.slice(0, colonIndex).trim();
      const value = line.slice(colonIndex + 1).trim();
      if (!rawKey || !value) return acc;
      const key = rawKey.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
      acc[key] = value;
      return acc;
    }, {});
}

function normalizeHrefLikeValue(value) {
  if (value == null) return "";
  return String(value).trim();
}

function resolveNodeHrefTarget(hrefValue, targetValue) {
  const href = normalizeHrefLikeValue(hrefValue);
  const target = normalizeHrefLikeValue(targetValue);
  if (href) {
    if (href.startsWith("#")) return { href, internalTarget: href.slice(1) || target || "" };
    if (/^[a-z][a-z0-9+.-]*:/i.test(href) || href.startsWith("//") || href.startsWith("/") || href.startsWith("?") || href.startsWith("./") || href.startsWith("../")) {
      return { href: safePageHref(href), internalTarget: "" };
    }
    return { href: `#${href}`, internalTarget: href };
  }
  if (target) return { href: `#${target}`, internalTarget: target };
  return { href: "#", internalTarget: "" };
}

const ANIMATIONS = {
  fadeIn: "pb-fadeIn 0.7s ease both", slideUp: "pb-slideUp 0.7s ease both",
  slideDown: "pb-slideDown 0.7s ease both", slideLeft: "pb-slideLeft 0.7s ease both",
  slideRight: "pb-slideRight 0.7s ease both", zoomIn: "pb-zoomIn 0.6s ease both",
  zoomOut: "pb-zoomOut 0.6s ease both", rotateIn: "pb-rotateIn 0.7s ease both",
  flipIn: "pb-flipIn 0.8s ease both", flipInY: "pb-flipInY 0.8s ease both",
  blurIn: "pb-blurIn 0.7s ease both", pulse: "pb-pulse 1.6s ease-in-out infinite",
  bounce: "pb-bounce 1.4s ease infinite", spin: "pb-spin 1.2s linear infinite",
  spinRev: "pb-spinRev 1.2s linear infinite", float: "pb-float 3s ease-in-out infinite",
  shake: "pb-shake 0.9s ease-in-out infinite", wobble: "pb-wobble 2s ease-in-out infinite",
  swing: "pb-swing 2s ease-in-out infinite", heartbeat: "pb-heartbeat 1.3s ease-in-out infinite",
  flash: "pb-flash 1.8s ease infinite",
};

const ANIMATION_KEYFRAMES = `
@keyframes pb-fadeIn{from{opacity:0}to{opacity:1}}
@keyframes pb-slideUp{from{opacity:0;transform:translateY(30px)}to{opacity:1;transform:translateY(0)}}
@keyframes pb-slideDown{from{opacity:0;transform:translateY(-30px)}to{opacity:1;transform:translateY(0)}}
@keyframes pb-slideLeft{from{opacity:0;transform:translateX(30px)}to{opacity:1;transform:translateX(0)}}
@keyframes pb-slideRight{from{opacity:0;transform:translateX(-30px)}to{opacity:1;transform:translateX(0)}}
@keyframes pb-zoomIn{from{opacity:0;transform:scale(0.8)}to{opacity:1;transform:scale(1)}}
@keyframes pb-zoomOut{from{opacity:0;transform:scale(1.2)}to{opacity:1;transform:scale(1)}}
@keyframes pb-rotateIn{from{opacity:0;transform:rotate(-12deg) scale(0.9)}to{opacity:1;transform:rotate(0) scale(1)}}
@keyframes pb-flipIn{from{opacity:0;transform:perspective(500px) rotateX(60deg)}to{opacity:1;transform:perspective(500px) rotateX(0)}}
@keyframes pb-flipInY{from{opacity:0;transform:perspective(500px) rotateY(60deg)}to{opacity:1;transform:perspective(500px) rotateY(0)}}
@keyframes pb-blurIn{from{opacity:0;filter:blur(8px)}to{opacity:1;filter:blur(0)}}
@keyframes pb-pulse{0%,100%{transform:scale(1)}50%{transform:scale(1.06)}}
@keyframes pb-bounce{0%,100%{transform:translateY(0)}50%{transform:translateY(-12px)}}
@keyframes pb-spin{to{transform:rotate(360deg)}}
@keyframes pb-spinRev{to{transform:rotate(-360deg)}}
@keyframes pb-float{0%,100%{transform:translateY(0)}50%{transform:translateY(-10px)}}
@keyframes pb-shake{0%,100%{transform:translateX(0)}25%{transform:translateX(-5px)}75%{transform:translateX(5px)}}
@keyframes pb-wobble{0%,100%{transform:rotate(0)}25%{transform:rotate(-3deg)}75%{transform:rotate(3deg)}}
@keyframes pb-swing{0%,100%{transform:rotate(-8deg)}50%{transform:rotate(8deg)}}
@keyframes pb-heartbeat{0%,100%{transform:scale(1)}25%{transform:scale(1.1)}40%{transform:scale(1)}60%{transform:scale(1.08)}}
@keyframes pb-flash{0%,50%,100%{opacity:1}25%,75%{opacity:0.3}}`;

function buildCustomAnim(node) {
  const p = node.animParams || {};
  const kind = p.kind || "translateX";
  const pattern = p.pattern || "backforth";
  const amp = p.amp != null && p.amp !== "" ? Number(p.amp) : 30;
  const unit = kind === "rotate" ? "deg" : "px";
  const fn = (v) => kind === "rotate" ? `rotate(${v}${unit})` : (kind === "translateY" ? `translateY(${v}${unit})` : `translateX(${v}${unit})`);
  let frames;
  if (pattern === "0toX") frames = `from{transform:${fn(0)}}to{transform:${fn(amp)}}`;
  else if (pattern === "-XtoX") frames = `from{transform:${fn(-amp)}}to{transform:${fn(amp)}}`;
  else if (pattern === "Xto-X") frames = `from{transform:${fn(amp)}}to{transform:${fn(-amp)}}`;
  else frames = `0%,100%{transform:${fn(-amp)}}50%{transform:${fn(amp)}}`;
  const name = `pb-cust-${node.id}`;
  const dur = node.animDuration || "1.2s";
  const delay = node.animDelay ? ` ${node.animDelay}` : "";
  const ease = pattern === "backforth" ? "ease-in-out" : "ease";
  const loop = p.loop === false ? "both" : "infinite";
  return { name, keyframe: `@keyframes ${name}{${frames}}`, css: `${name} ${dur} ${ease}${delay} ${loop}` };
}

function toEmbedUrl(url) {
  const safeUrl = safePageResourceUrl(url);
  if (!safeUrl) return "";
  const yt = safeUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vm = safeUrl.match(/vimeo\.com\/(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return safeUrl;
}

// --- Helpers de data-binding (mêmes que dans le builder) ---
function resolvePath(obj, path) {
  if (!path) return obj;
  return path.split(".").reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);
}

// =============================================================================
//  hydrateManualHtmlWithPageData(html, pageData, sourceKey) -- "HTML manuel +
//  PageData" mission (audit §1-23 in the conversation).
//
//  PROBLEM: a manually-edited page (Edit HTML Code, see PageHtmlSourceModal.jsx)
//  is served via ManualHtmlPageFrame's `srcDoc={html}` -- the saved HTML
//  STRING verbatim. That string is generated once, at Save time, by
//  buildSiteHtml() (PageBuilder.jsx): a bindKey/repeatBind-aware node walks
//  whatever data was LIVE in the Builder that day. Once saved, it is frozen
//  text -- any bound value it contains (a section's title, `contentPage.
//  pageTitle`, an image/video URL) is a SNAPSHOT, not a live read, so a
//  language switch or a database edit afterward never reaches it.
//
//  FIX: PageData (the SAME prop PageRenderer already receives and uses for
//  the normal JSON-tree render path below -- see `runtimeData`/DataContext)
//  is now ALSO the source of truth for the manual-HTML path. This function
//  re-hydrates the saved HTML's bound nodes against the CURRENT `pageData`
//  every time either changes (see ManualHtmlPageFrame's own useMemo) --
//  entirely in React, never inside the iframe (task spec §13: the iframe
//  must never know MongoDB/fetch anything itself).
//
//  HOW NODES ARE FOUND: buildSiteHtml() already wraps EVERY node (see
//  nodeToHtml() in PageBuilder.jsx) in `<... data-pb-node-id="<node.id>">`
//  AND embeds the exact JSON block tree that produced this HTML in
//  `<script id="pb-pagebuilder-document" type="application/json">` (the same
//  script PageBuilder.jsx's own "Import HTML" feature already reads). This
//  function reads that ONE existing script tag -- never a second, invented
//  representation -- to know which `data-pb-node-id` carries which
//  `bindKey`/`repeatBind`, then rewrites ONLY those DOM nodes' text/src/href,
//  via the standard DOM API (never string concatenation into markup, so
//  nothing here can itself inject unescaped HTML -- task spec §14).
//
//  The exact binding semantics mirror RenderNode's own (this same file,
//  ~L.1093/1740 for absolute bindKey resolution, ~L.1612+ for repeatBind) --
//  intentionally the SAME rules, not a second implementation to keep in
//  sync: `key === sourceKey || key.startsWith(sourceKey + ".")` then
//  `resolvePath(pageData, key.slice(sourceKey.length + 1))` for a root-level
//  bind; a repeat child's bindKey ("title", "text.0", "imagePath", ...)
//  resolves relative to the current array item instead.
//
//  READ-ONLY (task spec §16): this function only ever returns a new HTML
//  STRING for `srcDoc` -- it never calls any save/update API, never mutates
//  `html`/`pageData` themselves, and the original saved HTML (the Family-
//  authored TEMPLATE/DESIGN) is never touched, so restoring/re-editing it
//  later always gets the untouched original back.
//
//  BACKWARD COMPATIBLE (task spec §21): a saved HTML string with no
//  `#pb-pagebuilder-document` script (an older manual page, or one authored
//  entirely by hand with no PageBuilder bindings at all) is returned
//  UNCHANGED -- this function is a pure opt-in enhancement, never a
//  behavior change for pages that never had bindings to begin with.
// =============================================================================
// Named export (alongside the default PageRenderer export) purely so tests
// can exercise the hydration logic directly against a saved HTML string,
// without needing to mount the whole component tree -- see this file's own
// header comment on this function for the full rationale.
export function hydrateManualHtmlWithPageData(html, pageData, sourceKey) {
  if (typeof html !== "string" || !html.trim()) return html;
  if (typeof window === "undefined" || typeof DOMParser === "undefined") return html;
  // No PageData loaded yet (or this render's pageMode has none, e.g. a
  // "static" page) -- nothing to hydrate WITH, so the saved template's own
  // baked-in content is the only sensible thing to show (task spec §17:
  // "en mode editor/template preview -> fallback autorisé").
  if (!pageData || typeof pageData !== "object" || !sourceKey) return html;

  let dom;
  let parsedDoc;
  try {
    dom = new DOMParser().parseFromString(html, "text/html");
    const scriptEl = dom.getElementById("pb-pagebuilder-document");
    if (!scriptEl?.textContent?.trim()) return html; // no bindings embedded -- plain HTML, unchanged
    parsedDoc = JSON.parse(scriptEl.textContent);
  } catch (_e) {
    // Malformed/foreign HTML or embed -- fail safe, never break the page
    // over a hydration attempt (same "never crash the caller" convention as
    // every other resolver in this codebase).
    return html;
  }
  if (!parsedDoc || !Array.isArray(parsedDoc.pages)) return html;

  const escAttr = (s) => String(s).replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const isFileVideo = (url) => /\.(mp4|webm|ogg|ogv|mov|m4v)(\?|#|$)/i.test(url || "");
  const isEmptyValue = (val) => val === undefined || val === null || val === "" || (Array.isArray(val) && val.length === 0);

  // Every node's own `data-pb-node-id` is emitted TWICE (nodeToHtml's outer
  // `<div style="display:contents">` wrapper, AND once more inline on the
  // node's own real element) -- prefer the real element; a video/media node
  // with no placeholder url at all only ever HAS the wrapper (buildSiteHtml
  // emits an empty inner for it), so falling back to the wrapper is correct
  // there too (its innerHTML gets replaced wholesale, see hydrateVideoInto).
  const pickRealElement = (nodeId, root) => {
    const list = root.querySelectorAll(`[data-pb-node-id]`);
    let wrapper = null;
    for (const el of list) {
      if (el.getAttribute("data-pb-node-id") !== nodeId) continue;
      const styleAttr = (el.getAttribute("style") || "").replace(/\s+/g, "");
      if (styleAttr === "display:contents;") { wrapper = wrapper || el; continue; }
      return el;
    }
    return wrapper;
  };

  const hydrateVideoInto = (el, url) => {
    if (!el) return;
    const clean = url == null ? "" : String(url).trim();
    if (!clean) { el.innerHTML = ""; return; }
    el.innerHTML = isFileVideo(clean)
      ? `<video src="${escAttr(toFileUrl(clean))}" controls style="width:100%;height:100%;border:none;"></video>`
      : `<iframe src="${escAttr(toEmbedUrl(clean))}" title="video" allowfullscreen style="width:100%;height:100%;border:none;"></iframe>`;
  };

  // Applies ONE resolved value to the DOM element standing in for `node`,
  // mirroring RenderNode's own per-type behavior (image -> src, video ->
  // real <video>/<iframe> markup, button/link -> text+href, else -> text) --
  // task spec §8/§9/§10: an empty/absent value hides the element cleanly
  // (never a broken image, a black rectangle, or leftover vertical space),
  // while `0` (a real, valid value) is never treated as empty.
  const applyBinding = (el, node, value) => {
    if (node.type === "image") {
      if (!el) return;
      const src = value == null ? "" : String(value).trim();
      if (!src) { el.remove(); return; }
      el.setAttribute("src", src);
      return;
    }
    if (node.type === "video") {
      if (!el) return;
      hydrateVideoInto(el, value);
      return;
    }
    if (!el) return;
    if (isEmptyValue(value)) { el.remove(); return; }
    if (node.type === "button" || node.type === "link") {
      el.textContent = String(value);
      if (typeof value === "string" && /^https?:\/\//.test(value)) el.setAttribute("href", value);
      return;
    }
    if (node.type === "input" || node.type === "textarea") {
      el.setAttribute("value", String(value));
      return;
    }
    el.textContent = String(value);
  };

  // Root-level (non-repeat) bindKey -- e.g. "contentPage.pageTitle" resolves
  // against `pageData` itself once the `sourceKey` namespace prefix is
  // stripped, EXACTLY as RenderNode's own absolute-bindKey branch does.
  const resolveRootBinding = (key) =>
    key === sourceKey || key.startsWith(sourceKey + ".")
      ? resolvePath(pageData, key.slice(sourceKey.length + 1))
      : undefined;

  // Same per-node hydration as the root walk below, but scoped to ONE
  // repeat clone (`cloneRoot`, a detached subtree -- data-pb-node-id is
  // duplicated identically across every clone, so this must search only
  // WITHIN this clone, never the whole document) and resolved relative to
  // THIS item (a repeat child's bindKey -- "title", "text.0", "imagePath" --
  // is always relative, never prefixed with `sourceKey`, exactly as
  // fillNodeWithItem's own live-preview resolution works).
  const hydrateRepeatChild = (node, cloneRoot, item) => {
    if (node.bindKey) {
      applyBinding(pickRealElement(node.id, cloneRoot), node, resolvePath(item, node.bindKey));
    }
    (node.children || []).forEach((c) => hydrateRepeatChild(c, cloneRoot, item));
  };

  // REPEATBIND (task spec §6): the saved HTML holds exactly ONE static copy
  // of the repeated section's own template markup (buildSiteHtml never
  // expands repeatBind at export time, unlike the live JSON-tree preview) --
  // duplicate it once per REAL `pageData` item, hydrate each copy against
  // ITS OWN item, and remove the original template copy first so the count
  // always matches the database exactly (no leftover/duplicate panel).
  const hydrateRepeat = (node) => {
    const rb = node.repeatBind;
    if (rb.source !== sourceKey) return; // a different data domain -- leave this section's own static markup untouched
    const templateEl = pickRealElement(node.id, dom);
    if (!templateEl || !templateEl.parentNode) return;
    let items = resolvePath(pageData, rb.path);
    items = Array.isArray(items) ? items : [];
    const parent = templateEl.parentNode;
    const anchor = dom.createComment(`pb-repeat-${node.id}`);
    parent.insertBefore(anchor, templateEl);
    templateEl.remove();
    items.forEach((item) => {
      const clone = templateEl.cloneNode(true);
      parent.insertBefore(clone, anchor);
      (node.children || []).forEach((c) => hydrateRepeatChild(c, clone, item));
    });
    anchor.remove();
  };

  const walk = (node) => {
    if (!node || typeof node !== "object") return;
    if (node.repeatBind && node.repeatBind.source && node.repeatBind.path) {
      hydrateRepeat(node);
      return; // the repeat above hydrates its OWN children per clone -- never also walk them here
    }
    if (node.bindKey) {
      applyBinding(pickRealElement(node.id, dom), node, resolveRootBinding(node.bindKey));
    }
    (node.children || []).forEach(walk);
  };

  parsedDoc.pages.forEach((page) => (page.children || []).forEach(walk));

  return "<!doctype html>\n" + dom.documentElement.outerHTML;
}

let _dupCounter = 0;
function cloneNodeFresh(node) {
  const fresh = JSON.parse(JSON.stringify(node));
  const reid = (n) => { n._tplId = n.id; n.id = n.id + "_r" + (++_dupCounter); n.children?.forEach(reid); };
  reid(fresh);
  return fresh;
}

function guessBindKey(node) {
  if (node.type === "image") return "image|img|src|urlImg|ProductImageUrls.0|galeriesID.galeries.0.urlImg";
  if (node.type === "video") return "video|videoPath";
  const t = (node.text || node.field?.label || "").toLowerCase();
  if (/prix|price|montant|amount/i.test(t)) return "price";
  if (/nom|name|titre|title/i.test(t)) return "name";
  if (/desc/i.test(t)) return "description";
  if (/star|note|rating/i.test(t)) return "Stars";
  return null;
}

const mediaString = (value) => (value === undefined || value === null ? "" : String(value).trim());
const isImageNode = (node) => node?.type === "image" || node?.tag === "img";
const isVideoNode = (node) => node?.type === "video";

function fillNodeWithItem(node, item, fieldMap, autoFill, rootData, rootKey) {
  if (!node) return node;
  const out = { ...node };
  const resolveActionToken = (raw) => {
    if (raw == null) return raw;
    if (typeof raw !== "string") return raw;
    const value = raw.trim();
    if (!value) return raw;

    const normalizePath = (p) => (p.startsWith("items.") ? p.slice(6) : p.startsWith("item.") ? p.slice(5) : null);
    const resolveItemTokenValue = (tokenPathRaw) => {
      const tokenPath = String(tokenPathRaw || "").trim();
      if (!tokenPath) return undefined;
      const normalized = normalizePath(tokenPath);
      if (normalized) {
        const resolved = resolvePath(item, normalized);
        if (resolved !== undefined) return resolved;
      }
      const direct = resolvePath(item, tokenPath);
      if (direct !== undefined) return direct;
      const itemPath = normalized || tokenPath;
      if (["categoryID", "categoryId", "catID"].includes(itemPath)) return getShopCategoryId(item);
      if (["cateryName", "categoryName"].includes(itemPath)) return getShopCategoryName(item);
      if (tokenPath === "productId") return item?.productId || item?._id || item?.productID || item?.id;
      if (tokenPath === "urlFil") return item?.urlFil ?? item?.urlFilter ?? item?.filterUrl ?? "";
      return undefined;
    };

    const plainTokenValue = resolveItemTokenValue(value);
    if (plainTokenValue !== undefined) return plainTokenValue;

    if (value.includes("{{")) {
      return raw.replace(/\{\{\s*(items?|item)\.([^}]+)\s*\}\}/g, (_m, _prefix, path) => {
        const v = resolveItemTokenValue(path);
        return v == null ? "" : String(v);
      });
    }
    if (value.includes("${")) {
      return raw.replace(/\$\{\s*([^}]+)\s*\}/g, (_m, path) => {
        const v = resolveItemTokenValue(path);
        return v == null ? "" : String(v);
      });
    }
    return raw;
  };
  let manualKey = null;
  if (fieldMap) {
    for (const [k, targetId] of Object.entries(fieldMap)) {
      if (targetId === (node._tplId || node.id)) { manualKey = k; break; }
    }
  }
  const key = manualKey || node.bindKey || (autoFill && item ? guessBindKey(node) : null);
  if (key) {
    // Chemin absolu (commence par la racine) -> résoudre depuis rootData ;
    // sinon chemin relatif à l'item courant.
    let val;
    if (rootKey && (key === rootKey || key.startsWith(rootKey + "."))) {
      val = resolvePath(rootData, key.slice(rootKey.length + 1));
    } else if (item) {
      val = resolvePath(item, key);
    } else {
      val = resolvePath(rootData, key);
    }
    if (val !== undefined) {
      if (isImageNode(node)) {
        const src = mediaString(val);
        out.src = src;
        out._hideWhenEmptyMedia = !src;
      }
      else if (isVideoNode(node)) {
        const url = mediaString(val);
        out.videoUrl = url;
        out.options = { ...(node.options || {}), url };
        out._hideWhenEmptyMedia = !url;
      }
      else if (val != null) {
        if (node.type === "button") { out.text = String(val); if (typeof val === "string" && /^https?:\/\//.test(val)) out.href = String(val); }
        else if (node.type === "link") { out.text = String(val); if (typeof val === "string" && /^https?:\/\//.test(val)) out.href = String(val); }
        else if (node.type === "input" || node.type === "textarea") out.field = { ...(node.field || {}), value: String(val) };
        else if (node.type === "select") out.options = { ...(node.options || {}), choices: Array.isArray(val) ? val.map(String) : [String(val)] };
        else out.text = String(val);
      }
    }
  }
  // Support des actions clic dynamiques dans les éléments mappés.
  out.clickActionArg1 = resolveActionToken(out.clickActionArg1);
  out.clickActionArg2 = resolveActionToken(out.clickActionArg2);
  out.clickActionHref = resolveActionToken(out.clickActionHref);
  out.clickActionPage = resolveActionToken(out.clickActionPage);
  out.__mapItem = item || out.__mapItem;
  if (node.children) out.children = node.children.map((c) => fillNodeWithItem(c, item, fieldMap, autoFill, rootData, rootKey));
  return out;
}

// ---------------------------------------------------------------------------
// Composants riches (lecture seule)
// ---------------------------------------------------------------------------
// ── Swiper (carrousel) — supporte node.items ET node.slides, overlay, autoplay ──
function Swiper({ node }) {
  const ns = effStyle(node, useContext(DeviceContext));
  // Compatibilité : items (nouveau format PageBuilder) OU slides (ancien format)
  const items = node.items || node.slides || [];
  const o  = node.options   || {};
  const is = node.itemStyle || {};
  const n  = items.length || 1;
  const [idx, setIdx] = useState(0);
  const [visited, setVisited] = useState(() => new Set([0]));
  useEffect(() => { setVisited((previous) => previous.has(idx) ? previous : new Set([...previous, idx])); }, [idx]);
  const go = (d) => setIdx((p) => (p + d + n) % n);

  useEffect(() => { if (idx >= n) setIdx(0); }, [n, idx]);
  useEffect(() => {
    if (!o.autoplay || n < 2) return;
    const t = setInterval(() => setIdx((p) => (p + 1) % n), o.interval || 5000);
    return () => clearInterval(t);
  }, [n, o.autoplay, o.interval]);

  if (!items.length) return null;

  return (
    <div style={{ position: "relative", ...ns }}>
      <div style={{ position: "relative", height: o.height || "300px", overflow: "hidden" }}>
        {items.map((it, i) => (
          <div key={it.id || i} style={{
            position: "absolute", inset: 0,
            opacity: i === idx ? 1 : 0,
            transition: "opacity 0.5s ease",
            pointerEvents: i === idx ? "auto" : "none",
            backgroundImage: `linear-gradient(${is.overlay || "rgba(0,0,0,0.38)"},${is.overlay || "rgba(0,0,0,0.38)"}),url(${(i === idx || visited.has(i)) ? getOptimizedImage(it.image, { preset: "content" }) || "" : ""})`,
            backgroundSize: "cover", backgroundPosition: "center",
            display: "flex", flexDirection: "column",
            justifyContent: "center", alignItems: "center",
            textAlign: "center", padding: "32px",
          }}>
            {it.title && <h3 style={{ fontSize: "30px", fontWeight: 800, color: is.titleColor || "#fff", margin: "0 0 10px", lineHeight: 1.2 }}>{it.title}</h3>}
            {it.text  && <p  style={{ fontSize: "16px", color: is.textColor || "#eee", maxWidth: "560px", margin: 0, lineHeight: 1.6 }}>{it.text}</p>}
          </div>
        ))}
      </div>
      {/* Flèches */}
      {o.showArrows !== false && n > 1 && <>
        <button onClick={() => go(-1)} aria-label={elakayAutoText("h_a527f171f7b688", "Précédent")} style={{ position:"absolute", top:"50%", left:"14px", transform:"translateY(-50%)", background:"rgba(0,0,0,0.35)", border:"none", borderRadius:"50%", width:"36px", height:"36px", color:"#fff", fontSize:"20px", cursor:"pointer", zIndex:2, display:"flex", alignItems:"center", justifyContent:"center" }}>‹</button>
        <button onClick={() => go(1)}  aria-label={elakayAutoText("h_596d29a7e33c76", "Suivant")}   style={{ position:"absolute", top:"50%", right:"14px", transform:"translateY(-50%)", background:"rgba(0,0,0,0.35)", border:"none", borderRadius:"50%", width:"36px", height:"36px", color:"#fff", fontSize:"20px", cursor:"pointer", zIndex:2, display:"flex", alignItems:"center", justifyContent:"center" }}>›</button>
      </>}
      {/* Dots */}
      {o.showDots !== false && n > 1 && (
        <div style={{ position:"absolute", bottom:"12px", left:0, right:0, display:"flex", justifyContent:"center", gap:"6px", zIndex:2 }}>
          {items.map((_, i) => (
            <button key={i} onClick={() => setIdx(i)} aria-label={elakayAutoText("h_4d12690c63f59a", "Diapo {value0}", { value0: i+1 })}
              style={{ width: i===idx?"20px":"10px", height:"10px", borderRadius:"10px", border:"none", background: i===idx ? "#fff" : "rgba(255,255,255,0.5)", cursor:"pointer", transition:"width 0.2s", padding:0 }} />
          ))}
        </div>
      )}
    </div>
  );
}

function getShopOwnerId(storeData) {
  return (
    resolvePath(storeData, "owner._id") ||
    resolvePath(storeData, "owner.id") ||
    resolvePath(storeData, "categories.owner") ||
    resolvePath(storeData, "pubCategoryProdMatrix.owner") ||
    resolvePath(storeData, "shopPubMatrix.owner") ||
    null
  );
}

function normalizeShopCategoryId(value) {
  if (value == null) return null;
  if (Array.isArray(value)) {
    for (const item of value) {
      const normalized = normalizeShopCategoryId(item);
      if (normalized) return normalized;
    }
    return null;
  }
  if (typeof value === "object") {
    return normalizeShopCategoryId(
      value._id ?? value.id ?? value.categoryID ?? value.categoryId ?? value.catID
    );
  }
  const normalized = String(value).trim();
  return normalized && normalized !== "[object Object]" ? normalized : null;
}

function normalizeShopUserId(value) {
  return normalizeShopCategoryId(value);
}

function getFollowEntryId(entry) {
  if (entry == null) return null;
  if (typeof entry !== "object") return normalizeShopUserId(entry);
  return normalizeShopUserId(
    entry._id ??
    entry.id ??
    entry.userID ??
    entry.userId ??
    entry.owner
  );
}

function isShopFollowed(followList, shopId) {
  if (!shopId || !Array.isArray(followList)) return false;
  return followList.some((entry) => String(entry?.shopId || "") === String(shopId));
}

function getAdminUserIds(userAdmin) {
  const list = Array.isArray(userAdmin) ? userAdmin : userAdmin ? [userAdmin] : [];
  return list.map(getFollowEntryId).filter(Boolean);
}

function getShopCategoryIds(category) {
  if (!category || typeof category !== "object") return [];
  const values = [
    category.categoryID,
    category.categoryId,
    category.catID,
    category._id,
    category.id,
    ...(Array.isArray(category.categoryIDList) ? category.categoryIDList : []),
    ...(Array.isArray(category.gategoriesLink) ? category.gategoriesLink : []),
  ];
  return [...new Set(values.map(normalizeShopCategoryId).filter(Boolean))];
}

function getShopCategoryId(category) {
  return getShopCategoryIds(category)[0] || null;
}

function getShopCategoryName(category) {
  const raw =
    category?.cateryName ??
    category?.categoryName ??
    category?.item_name ??
    category?.label ??
    category?.title ??
    category?.name;
  if (Array.isArray(raw)) return raw.find((value) => String(value || "").trim()) || "All Products";
  return String(raw || "").trim() || "All Products";
}

function getStoreCategories(storeData) {
  const categories = resolvePath(storeData, "categories.categories");
  return Array.isArray(categories) ? categories : [];
}

function getFirstTextStyle(node, device) {
  if (!node || typeof node !== "object") return {};
  if (["text", "button", "link"].includes(node.type) || ["p", "span", "h1", "h2", "h3", "h4", "h5", "h6"].includes(node.tag)) {
    return effStyle(node, device);
  }
  for (const child of node.children || []) {
    const style = getFirstTextStyle(child, device);
    if (Object.keys(style).length > 0) return style;
  }
  return {};
}

// withToggleBackgroundColor moved to pageRendererStyleUtils.js (see that
// file's own header comment) so it's unit-testable -- this file itself
// transitively imports "swiper/react", which Jest cannot resolve, so
// nothing exported from here could be tested directly.

function getToggleVisualStyle(node, device) {
  const style = effStyle(node, device);
  const textStyle = getFirstTextStyle(node, device);
  const visual = {};
  [
    "background",
    "backgroundColor",
    "border",
    "borderColor",
    "borderStyle",
    "borderWidth",
    "borderRadius",
    "boxShadow",
    "outline",
    "opacity",
  ].forEach((key) => {
    if (style[key] != null) visual[key] = style[key];
  });
  if (textStyle.color != null) visual.color = textStyle.color;
  else if (style.color != null) visual.color = style.color;
  if (textStyle.fontWeight != null) visual.fontWeight = textStyle.fontWeight;
  else if (style.fontWeight != null) visual.fontWeight = style.fontWeight;
  if (textStyle.fontSize != null) visual.fontSize = textStyle.fontSize;
  if (textStyle.textDecoration != null) visual.textDecoration = textStyle.textDecoration;
  return withToggleBackgroundColor(visual);
}

function collectToggleGroupStyles(nodes, device) {
  const groups = {};
  const walk = (node) => {
    if (!node || typeof node !== "object") return;
    const toggle = node.toggleGroup;
    if (toggle?.id) {
      if (!groups[toggle.id]) {
        groups[toggle.id] = {
          activeStyle: { ...(toggle.activeStyle || {}) },
          inactiveStyle: { ...(toggle.inactiveStyle || {}) },
          hasDefaultVisual: false,
          hasInactiveVisual: false,
        };
      }
      const group = groups[toggle.id];
      const visual = getToggleVisualStyle(node, device);
      if (toggle.isDefault) {
        group.activeStyle = { ...group.activeStyle, ...visual };
        group.hasDefaultVisual = true;
      } else if (!group.hasInactiveVisual) {
        group.inactiveStyle = { ...group.inactiveStyle, ...visual };
        group.hasInactiveVisual = true;
      }
    }
    (node.children || []).forEach(walk);
  };
  (nodes || []).forEach(walk);
  return groups;
}

function withRuntimeCategoryToggleGroups(nodes, device) {
  const migrate = (node) => {
    if (!node || typeof node !== "object") return node;
    let children = (node.children || []).map(migrate);
    const defaultIndex = children.findIndex((child) =>
      child?.clickActionFunction === "loadProductsByArrival_" &&
      !child?.repeatBind
    );
    const categoryIndex = children.findIndex((child) =>
      child?.repeatBind?.source === "store" &&
      /categor/i.test(String(child?.repeatBind?.path || ""))
    );

    if (defaultIndex >= 0 && categoryIndex >= 0) {
      const defaultNode = children[defaultIndex];
      const categoryNode = children[categoryIndex];
      const groupId =
        defaultNode?.toggleGroup?.id ||
        categoryNode?.toggleGroup?.id ||
        `runtime-category-${node.id || defaultNode.id || categoryNode.id}`;
      const activeStyle = {
        ...(defaultNode?.toggleGroup?.activeStyle || {}),
        ...getToggleVisualStyle(defaultNode, device),
      };
      const inactiveStyle = {
        ...(categoryNode?.toggleGroup?.inactiveStyle || {}),
        ...getToggleVisualStyle(categoryNode, device),
      };

      children = children.map((child, index) => {
        if (index !== defaultIndex && index !== categoryIndex) return child;
        return {
          ...child,
          toggleGroup: {
            id: groupId,
            isDefault: index === defaultIndex,
            activeStyle,
            inactiveStyle,
          },
        };
      });
    }

    return Array.isArray(node.children) ? { ...node, children } : node;
  };

  return (nodes || []).map(migrate);
}

function hasRuntimeShopFollowButton(nodes = []) {
  return nodes.some((node) =>
    node?.shopFollowButton ||
    node?.clickActionFunction === "clickToFollowShop" ||
    hasRuntimeShopFollowButton(node?.children || [])
  );
}

function makeRuntimeShopFollowBar(templateKey = "store") {
  const darkTemplates = new Set(["premium", "tech", "sport", "luxe", "nuit", "urban", "matrixNeon"]);
  const isDark = darkTemplates.has(templateKey);
  const accent = isDark ? "#d4af37" : "#6d28d9";
  const surface = isDark ? "#111827" : "#ffffff";
  const followStyle = {
    background: accent,
    backgroundColor: accent,
    color: isDark ? "#111827" : "#ffffff",
    border: `1px solid ${accent}`,
  };
  return {
    id: `runtime-follow-bar-${templateKey}`,
    type: "section",
    shopFollowBar: true,
    style: {
      padding: "10px 24px",
      background: surface,
      borderBottom: `1px solid ${isDark ? "#374151" : "#ece9f5"}`,
      display: "flex",
      justifyContent: "flex-end",
      alignItems: "center",
      minHeight: "54px",
    },
    children: [{
      id: `runtime-follow-button-${templateKey}`,
      type: "button",
      text: elakayAutoText("h_66587a7a62a90f", "Follow"),
      shopFollowButton: true,
      clickActionType: "function",
      clickActionFunction: "clickToFollowShop",
      followStyle,
      unfollowStyle: {
        background: "transparent",
        backgroundColor: "transparent",
        color: accent,
        border: `1px solid ${accent}`,
      },
      style: {
        minWidth: "126px",
        padding: "9px 20px",
        fontSize: "13px",
        fontWeight: 700,
        borderRadius: "50px",
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "background-color 0.2s ease,color 0.2s ease,border-color 0.2s ease",
        ...followStyle,
      },
    }],
  };
}

function withRuntimeShopFollowButton(nodes = [], templateKey = null) {
  if (!templateKey || hasRuntimeShopFollowButton(nodes)) return nodes;
  const followBar = makeRuntimeShopFollowBar(templateKey);
  return nodes.length ? [nodes[0], followBar, ...nodes.slice(1)] : [followBar];
}

function matchesShopCategory(product, categoryId) {
  const wanted = normalizeShopCategoryId(categoryId);
  if (!wanted) return true;
  const categories = Array.isArray(product?.categoriesID) ? product.categoriesID : [];
  return categories.some((entry) => getShopCategoryIds(
    entry && typeof entry === "object" ? entry : { categoryID: entry }
  ).includes(wanted));
}

function matchesShopSearch(product, searchText) {
  const query = normalizeHrefLikeValue(searchText).toLowerCase();
  if (!query) return true;
  const name = Array.isArray(product?.name) ? product.name[0] : product?.name;
  return normalizeHrefLikeValue(name).toLowerCase().includes(query);
}

function toSafeFilters(filtersObj) {
  const raw = filtersObj && typeof filtersObj === "object" ? filtersObj : {};
  const nested = raw.filters && typeof raw.filters === "object" ? raw.filters : raw;
  return { filters: nested };
}

// ── Stat ──────────────────────────────────────────────────────────────────────
function StatBlock({ node }) {
  const ns = effStyle(node, useContext(DeviceContext));
  const c = node.content || {};
  // bindKey peut avoir injecté la valeur dans node.text — on l'utilise comme fallback
  const displayValue = node._resolvedValue ?? c.value ?? node.text ?? "";
  return (
    <div style={ns}>
      <div style={{ fontSize: "32px", fontWeight: 800, color: c.valueColor || "#6d28d9", lineHeight: 1.1 }}>{displayValue}</div>
      {c.label && <div style={{ fontSize: "13px", color: c.labelColor || "#7a73a0", marginTop: "5px" }}>{c.label}</div>}
    </div>
  );
}

// ── Rating ────────────────────────────────────────────────────────────────────
function RatingBlock({ node }) {
  const ns = effStyle(node, useContext(DeviceContext));
  const c = node.content || {};
  const max = c.max || 5;
  const val = Number(c.value) || 0;
  return (
    <div style={ns}>
      <span style={{ fontSize: "22px", color: c.color || "#f59e0b", letterSpacing: "2px" }}>
        {"★".repeat(Math.round(val))}{"☆".repeat(Math.max(0, max - Math.round(val)))}
      </span>
      {c.label && <span style={{ fontSize: "14px", color: "#7a73a0", marginLeft: "8px" }}>{c.label}</span>}
    </div>
  );
}

// ── Quote ─────────────────────────────────────────────────────────────────────
function QuoteBlock({ node }) {
  const ns = effStyle(node, useContext(DeviceContext));
  const c = node.content || {};
  return (
    <blockquote style={ns}>
      <p style={{ margin: "0 0 8px", fontSize: "18px", fontStyle: "italic", color: "inherit", lineHeight: 1.6 }}>{c.text}</p>
      {c.author && <footer style={{ fontSize: "14px", fontWeight: 700, color: "#6d28d9" }}>{c.author}</footer>}
    </blockquote>
  );
}

// ── TextGroup ─────────────────────────────────────────────────────────────────
function TextGroupBlock({ node }) {
  const ns = effStyle(node, useContext(DeviceContext));
  const items = node.items || [];
  return (
    <div style={ns}>
      {items.map((it) => {
        const Tag = it.tag || "p";
        return (
          <Tag key={it.id} style={{ margin: 0, fontSize: it.fontSize, fontWeight: it.fontWeight, color: it.color, letterSpacing: it.letterSpacing, lineHeight: 1.35 }}>
            {it.text}
          </Tag>
        );
      })}
    </div>
  );
}

// ── Card ──────────────────────────────────────────────────────────────────────
function CardBlock({ node, onNavigate }) {
  const ns = effStyle(node, useContext(DeviceContext));
  const o = node.options || {};
  const c = node.content || {};
  const landscape = o.orientation === "landscape";
  const imageRight = landscape && o.imageSide === "right";
  const img = c.image ? (
    <ElakayImage preset="content" src={c.image} alt={c.title || ""} style={{ display:"block", objectFit:"cover", width: landscape ? "42%" : "100%", height: landscape ? "100%" : (c.imageHeight || "180px"), alignSelf: landscape ? "stretch" : undefined }} />
  ) : null;
  const body = (
    <div style={{ padding: "16px", flex: landscape ? 1 : undefined }}>
      {c.title && <h4 style={{ fontSize: "17px", fontWeight: 700, color: c.titleColor || "#1a1a2e", margin: "0 0 6px" }}>{c.title}</h4>}
      {c.text  && <p  style={{ fontSize: "14px", color: c.textColor  || "#4a4a68", lineHeight: 1.6, margin: o.showButton ? "0 0 12px" : 0 }}>{c.text}</p>}
      {o.showButton && c.buttonText && (
        <a href={safePageHref(c.buttonHref)} style={{ display:"inline-block", padding:"8px 18px", fontSize:"13px", fontWeight:600, color:"#fff", background:"#6d28d9", borderRadius:"8px", textDecoration:"none" }}>{c.buttonText}</a>
      )}
    </div>
  );
  return (
    <div style={{ ...ns, display:"flex", flexDirection: landscape ? "row" : "column" }}>
      {imageRight ? <>{body}{img}</> : <>{img}{body}</>}
    </div>
  );
}

// ── Progress ─────────────────────────────────────────────────────────────────
function ProgressBlock({ node }) {
  const ns = effStyle(node, useContext(DeviceContext));
  const c = node.content || {};
  const pct = Math.max(0, Math.min(100, Number(c.percent) || 0));
  return (
    <div style={ns}>
      <div style={{ display:"flex", justifyContent:"space-between", fontSize:"13px", color:"#4a4a68", marginBottom:"6px" }}>
        <span>{c.label}</span><span style={{ fontWeight:700 }}>{pct}%</span>
      </div>
      <div style={{ height:"10px", borderRadius:"10px", background: c.trackColor || "#ece9f5", overflow:"hidden" }}>
        <div style={{ width:`${pct}%`, height:"100%", background: c.barColor || "#6d28d9", borderRadius:"10px" }} />
      </div>
    </div>
  );
}

function ModalBlock({ node, onNavigate }) {
  const ns = effStyle(node, useContext(DeviceContext));
  const [open, setOpen] = useState(false);
  const isSimple = node.contentMode === "simple";
  const panel = (
    <div
      style={{ ...ns, position:"relative", maxWidth:"min(100%,900px)",
        maxHeight:"calc(100vh - 40px)", overflowY:"auto" }}
      onClick={(e) => e.stopPropagation()}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        {node.title ? <strong style={{ fontSize: "20px", color: "#1a1a2e" }}>{node.title}</strong> : <span />}
        <button onClick={() => setOpen(false)} style={{ border: "none", background: "none", cursor: "pointer", color: "#9b93b8", fontSize: "20px" }} aria-label={elakayAutoText("h_5ab4ec640d654d", "Fermer")}>×</button>
      </div>
      {isSimple ? (
        <>
          {node.simple?.text && <p style={{ fontSize: "16px", color: "#4a4a68", lineHeight: 1.6, margin: 0 }}>{node.simple.text}</p>}
          {node.simple?.buttonText && (
            node.simple.buttonHref
              ? <a href={safePageHref(node.simple.buttonHref)} style={{ alignSelf: "flex-start", padding: "10px 22px", fontSize: "15px", fontWeight: 600, color: "#fff", background: "#6d28d9", borderRadius: "10px", textDecoration: "none" }}>{node.simple.buttonText}</a>
              : <button onClick={() => setOpen(false)} style={{ alignSelf: "flex-start", padding: "10px 22px", fontSize: "15px", fontWeight: 600, color: "#fff", background: "#6d28d9", border: "none", borderRadius: "10px", cursor: "pointer" }}>{node.simple.buttonText}</button>
          )}
        </>
      ) : (
        node.children?.map((c) => <RenderNode key={c.id} node={c} onNavigate={onNavigate} />)
      )}
    </div>
  );
  return (
    <>
      <button style={node.triggerStyle || {}} onClick={() => setOpen(true)}>{node.triggerText || elakayAutoText("h_42c07747756067", "Ouvrir")}</button>
      {open && (
        <ViewportModal>
          <div onClick={() => setOpen(false)}
            style={{ position:"fixed", inset:0, zIndex:13000, display:"flex", alignItems:"center",
              justifyContent:"center", overflowY:"auto", padding:"20px", background:"rgba(20,12,40,0.5)" }}>
            {panel}
          </div>
        </ViewportModal>
      )}
    </>
  );
}

function DropdownBlock({ node }) {
  const ns = effStyle(node, useContext(DeviceContext));
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const close = () => setOpen(false);
    if (open) { window.addEventListener("click", close); return () => window.removeEventListener("click", close); }
  }, [open]);
  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <button style={node.triggerStyle || {}} onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}>{node.triggerText || elakayAutoText("h_31dfa027034be3", "Menu ▾")}</button>
      {open && (
        <div style={{ ...ns, position: "absolute", marginTop: "6px", zIndex: 50 }}>
          {(node.items || []).map((it) => (
            <a key={it.id} href={safePageHref(it.href)} style={{ display: "block", padding: "9px 16px", fontSize: "14px", color: "#2a2547", textDecoration: "none" }}>{it.label}</a>
          ))}
        </div>
      )}
    </div>
  );
}

function TabsBlock({ node }) {
  const ns = effStyle(node, useContext(DeviceContext));
  const [active, setActive] = useState(0);
  const items = node.items || [];
  return (
    <div style={ns}>
      <div style={{ display: "flex", borderBottom: "1px solid #ece9f5", background: "#faf9fd" }}>
        {items.map((it, i) => (
          <button key={it.id} onClick={() => setActive(i)}
            style={{ padding: "12px 20px", borderTop: "none", borderRight: "none", borderLeft: "none", background: "none", cursor: "pointer", fontSize: "14px", fontWeight: 600, color: active === i ? "#6d28d9" : "#7a73a0", borderBottom: active === i ? "2px solid #6d28d9" : "2px solid transparent" }}>
            {it.label}
          </button>
        ))}
      </div>
      <div style={{ padding: "20px", fontSize: "15px", color: "#4a4a68", lineHeight: 1.6 }}>{items[active]?.text}</div>
    </div>
  );
}

function OffcanvasBlock({ node, onNavigate }) {
  const ns = effStyle(node, useContext(DeviceContext));
  const [open, setOpen] = useState(false);
  const side = ["left", "right", "top", "bottom"].includes(node.side) ? node.side : "right";
  const horizontal = side === "left" || side === "right";
  const panelPos = horizontal ? { position: "fixed", top: 0, bottom: 0, [side]: 0 } : { position: "fixed", left: 0, right: 0, [side]: 0 };
  return (
    <>
      <button style={node.triggerStyle || {}} onClick={() => setOpen(true)}>{node.triggerText || elakayAutoText("h_42c07747756067", "Ouvrir")}</button>
      {open && (
        <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(20,12,40,0.4)", zIndex: 998 }}>
          <div style={panelPos} onClick={(e) => e.stopPropagation()}>
            <div style={ns}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                {node.title ? <strong style={{ fontSize: "18px", color: "#1a1a2e" }}>{node.title}</strong> : <span />}
                <button onClick={() => setOpen(false)} style={{ border: "none", background: "none", cursor: "pointer", color: "#9b93b8", fontSize: "20px" }} aria-label={elakayAutoText("h_5ab4ec640d654d", "Fermer")}>×</button>
              </div>
              {node.children?.map((c) => <RenderNode key={c.id} node={c} onNavigate={onNavigate} />)}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function TooltipBlock({ node }) {
  const ns = effStyle(node, useContext(DeviceContext));
  const [hover, setHover] = useState(false);
  return (
    <span style={{ position: "relative", ...ns }} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      {node.text}
      {hover && node.tip && (
        <span style={{ position: "absolute", bottom: "calc(100% + 8px)", left: "50%", transform: "translateX(-50%)", background: "#1a1a2e", color: "#fff", padding: "6px 12px", borderRadius: "8px", fontSize: "13px", whiteSpace: "nowrap", zIndex: 60 }}>{node.tip}</span>
      )}
    </span>
  );
}

function AccordionBlock({ node }) {
  const ns = effStyle(node, useContext(DeviceContext));
  const [open, setOpen] = useState({});
  return (
    <div style={ns}>
      {(node.items || []).map((it) => (
        <div key={it.id} style={{ border: "1px solid #ece9f5", borderRadius: "8px", overflow: "hidden", marginBottom: "8px" }}>
          <button onClick={() => setOpen((s) => ({ ...s, [it.id]: !s[it.id] }))}
            style={{ width: "100%", display: "flex", justifyContent: "space-between", padding: "14px 18px", border: "none", background: "#fff", cursor: "pointer", fontSize: "15px", fontWeight: 600, color: "#2a2547", textAlign: "left" }}>
            {it.q} <span style={{ color: "#6d28d9" }}>{open[it.id] ? "−" : "+"}</span>
          </button>
          {open[it.id] && <div style={{ padding: "14px 18px", background: "#faf9fd", fontSize: "14px", color: "#4a4a68", lineHeight: 1.6 }}>{it.a}</div>}
        </div>
      ))}
    </div>
  );
}

// --- Zone de signature manuscrite (canvas) ---
// --- Positions de signature : coordonnées X/Y (% ou px) ---
function getSignPosStyleXY(x, y, unit = "%") {
  return unit === "%"
    ? { position: "absolute", left: `${x}%`, top: `${y}%`, transform: "translate(-50%,-50%)", zIndex: 2, width: 160 }
    : { position: "absolute", left: x, top: y, zIndex: 2, width: 160 };
}

function toAbsoluteUrl(url) {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  if (typeof window === "undefined") return url;
  try { return new URL(url, window.location.origin).href; } catch { return url; }
}

function toOfficeEmbedUrl(url) {
  const absolute = toAbsoluteUrl(url);
  if (!absolute) return "";
  return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(absolute)}`;
}

function inferDocKind(name = "", url = "") {
  const s = `${name} ${url}`.toLowerCase();
  if (s.includes(".pdf")) return "pdf";
  if (s.includes(".docx")) return "docx";
  if (s.includes(".doc")) return "doc";
  return "unknown";
}

function splitDocHtmlPages(docHtml = "") {
  if (!docHtml || typeof docHtml !== "string") return [];

  const styleBlocks = (docHtml.match(/<style[\s\S]*?<\/style>/gi) || []).join("");
  let content = docHtml.replace(/<style[\s\S]*?<\/style>/gi, "").trim();
  const wrapperMatch = content.match(/<div[^>]*class=(["'])[^"']*\bpb-wdoc\b[^"']*\1[^>]*>([\s\S]*)<\/div>/i);
  if (wrapperMatch?.[2]) content = wrapperMatch[2];

  const PAGE_BREAK_TOKEN = "__PB_PAGE_BREAK__";
  const separatorRegex = /<div[^>]*class=(["'])[^"']*\bpb-pg-sep\b[^"']*\1[^>]*>\s*<\/div>|<hr[^>]*class=(["'])[^"']*\bpb-pg-sep\b[^"']*\2[^>]*>|<br[^>]*clear=(["'])?all\3?[^>]*>/gi;
  const parts = content
    .replace(separatorRegex, PAGE_BREAK_TOKEN)
    .split(PAGE_BREAK_TOKEN)
    .map((part) => part.trim())
    .filter(Boolean);

  const pages = parts.length ? parts : [content];
  return pages.map((page) =>
    styleBlocks
      ? `${styleBlocks}<div class="pb-wdoc">${page}</div>`
      : `<div class="pb-wdoc">${page}</div>`
  );
}

function SignaturePad({ width = 380, height = 120, onChange }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  useEffect(() => {
    const c = canvasRef.current; if (!c) return;
    const ctx = c.getContext("2d");
    ctx.lineWidth = 2; ctx.lineCap = "round"; ctx.strokeStyle = "#1a1a2e";
  }, []);
  const pos = (e) => { const r = canvasRef.current.getBoundingClientRect(); const t = e.touches?.[0] || e; return { x: t.clientX - r.left, y: t.clientY - r.top }; };
  const start = (e) => { e.preventDefault(); drawing.current = true; last.current = pos(e); };
  const move = (e) => {
    if (!drawing.current) return; e.preventDefault();
    const ctx = canvasRef.current.getContext("2d"); const p = pos(e);
    ctx.beginPath(); ctx.moveTo(last.current.x, last.current.y); ctx.lineTo(p.x, p.y); ctx.stroke();
    last.current = p;
  };
  const end = () => { drawing.current = false; if (onChange) onChange(canvasRef.current.toDataURL("image/png")); };
  const clear = () => { const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); if (onChange) onChange(""); };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      <canvas ref={canvasRef} width={width} height={height}
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        style={{ border: "1.5px solid #e0daf5", borderRadius: "10px", background: "#fff", touchAction: "none", cursor: "crosshair", width: "100%", maxWidth: width }} />
      <button type="button" onClick={clear}
        style={{ alignSelf: "flex-start", padding: "5px 12px", fontSize: "12px", color: "#e11d48", background: "transparent", border: "1px solid #f3b4c2", borderRadius: "8px", cursor: "pointer" }}>{elakayAutoText("h_fe23de7b572d52", "Effacer")}</button>
    </div>
  );
}

// --- Visualiseur public : multi-pages Word, signatures multiples positionnées ---
function DocViewerBlock({ node, onSign }) {
  const ns = effStyle(node, useContext(DeviceContext));
  const dataCtx = useContext(DataContext);
  const [view, setView] = useState(node.defaultView || "html");
  const [signMode, setSignMode] = useState(node.signMode === "type" ? "type" : "draw");
  const sigs = node.signatures || [];

  const [sigData, setSigData] = useState(() =>
    sigs.map((s) => ({ id: s.id, image: "", name: "", date: "", signed: false }))
  );
  useEffect(() => {
    setSigData((prev) =>
      sigs.map((s) => prev.find((p) => p.id === s.id) || { id: s.id, image: "", name: "", date: "", signed: false })
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sigs.length]);

  // Résolution de la source du document (binding > nœud > vide)
  let bound = null;
  if (node.bindKey && dataCtx?.data && dataCtx?.sourceKey) {
    const key = node.bindKey;
    if (key === dataCtx.sourceKey || key.startsWith(dataCtx.sourceKey + ".")) {
      const val = resolvePath(dataCtx.data, key.slice(dataCtx.sourceKey.length + 1));
      if (val && typeof val === "object") bound = val;
    }
  }
  const docName = bound?.docName || node.docName || "Document";
  const docUrl  = safePageResourceUrl(bound?.docUrl || node.docUrl || "");
  const docHtml = bound?.docHtml || node.docHtml || "";
  // Préfère le kind persisté dans le nœud (plus fiable que la déduction par nom)
  const docKind = node.docKind || bound?.docKind || inferDocKind(docName, docUrl);
  const isPdf   = docKind === "pdf";
  const isWord  = docKind === "docx" || docKind === "doc";
  const docExt  = isPdf ? ".pdf" : docKind === "docx" ? ".docx" : ".doc";
  const officeEmbedUrl = isWord ? toOfficeEmbedUrl(docUrl) : "";
  const pdfPreviewUrl  = isPdf ? docUrl : "";
  const hasDoc  = !!(docUrl || docHtml);
  const fullPageMode = !!node.fullPageMode;
  const pageDisplayMode = node.pageDisplayMode === "count" ? "count" : "all";
  const pagesToShow = Math.max(1, Math.floor(Number(node.pagesToShow) || 1));
  const docHtmlPages = useMemo(() => splitDocHtmlPages(docHtml), [docHtml]);
  const totalDocPages = docHtmlPages.length;
  const visibleDocPages = pageDisplayMode === "count" ? docHtmlPages.slice(0, pagesToShow) : docHtmlPages;
  const shownPagesCount = pageDisplayMode === "count" ? Math.min(pagesToShow, totalDocPages) : totalDocPages;

  const allRequiredSigned = sigs.every((s, i) => !s.required || sigData[i]?.signed);
  const anySigned  = sigData.some((s) => s.signed);
  const signedCount   = sigData.filter((s) => s.signed).length;
  const requiredCount = sigs.filter((s) => s.required).length || sigs.length;
  const needsBlocking = node.requireSign && !allRequiredSigned;
  const lockedStyle = needsBlocking
    ? { filter: "blur(4px)", pointerEvents: "none", userSelect: "none", transition: "filter 0.35s ease" }
    : { filter: "none", transition: "filter 0.35s ease" };

  const confirmZone = (i) => {
    const zone = sigs[i];
    const data = sigData[i];
    if (!data) return;
    if (signMode === "draw" && !data.image) return;
    if (signMode === "type" && !data.name) return;
    setSigData((prev) => prev.map((s, j) => j === i ? { ...s, signed: true } : s));
    const payload = signMode === "type"
      ? { type: "text", name: data.name, date: data.date }
      : { type: "draw", image: data.image };
    if (onSign) onSign({ ...payload, zoneId: zone.id, zoneLabel: zone.label, docName, signedAt: new Date().toISOString() });
  };

  const tabBtn = (active) => ({
    padding: "7px 16px", fontSize: "12px", fontWeight: 600, cursor: "pointer",
    border: `1.5px solid ${active ? "#4f46e5" : "#e0daf5"}`,
    background: active ? "#4f46e5" : "#fff", color: active ? "#fff" : "#4f46e5",
    borderRadius: "8px", transition: "all 0.15s",
  });
  const rootStyle = fullPageMode
    ? {
        ...ns,
        width: "100vw",
        maxWidth: "100vw",
        marginLeft: "calc(50% - 50vw)",
        marginRight: "calc(50% - 50vw)",
      }
    : ns;
  const htmlPaneStyle = fullPageMode
    ? { padding: "14px 12px", maxHeight: "none", overflowY: "visible", display: "flex", flexDirection: "column", gap: "14px" }
    : { padding: "44px 60px", maxHeight: "70vh", overflowY: "auto", display: "flex", flexDirection: "column", gap: "22px" };
  const htmlInlinePaneStyle = fullPageMode
    ? { maxHeight: "none", overflowY: "visible", display: "flex", flexDirection: "column", gap: "14px" }
    : { fontSize: "15px", lineHeight: 1.95, color: "#1e293b", fontFamily: "'Georgia','Times New Roman',serif" };
  const renderDocHtml = (paneStyle) => {
    const pages = visibleDocPages.length ? visibleDocPages : [docHtml];
    return (
      <div style={{ ...paneStyle, ...lockedStyle }}>
        {pages.map((pageHtml, idx) => (
          <div
            key={`doc-page-${idx}`}
            style={
              totalDocPages > 1
                ? {
                    background: "#fff",
                    border: "1px solid #e2e8f0",
                    borderRadius: fullPageMode ? "8px" : "10px",
                    boxShadow: fullPageMode ? "0 1px 4px rgba(0,0,0,0.06)" : "0 2px 10px rgba(0,0,0,0.06)",
                    padding: fullPageMode ? "12px 10px" : "18px 14px",
                  }
                : {}
            }
          >
            <iframe
              title={`${docName || "Document"} ${idx + 1}`}
              srcDoc={securePageHtmlDocument(pageHtml)}
              sandbox={PAGE_HTML_SANDBOX}
              referrerPolicy="no-referrer"
              style={{ width: "100%", minHeight: fullPageMode ? "70vh" : "520px", border: 0, display: "block" }}
            />
          </div>
        ))}
      </div>
    );
  };

  return (
    <div style={rootStyle}>
      {/* ════════════════════════════════════════════════════════
          MODE INLINE  — contenu intégré directement dans la page
          MODE CARD    — visualiseur complet avec header/footer
          ════════════════════════════════════════════════════════ */}
      {node.viewStyle === "inline" ? (

        /* ── MODE INLINE ─────────────────────────────────────── */
        <div>
          {/* Barre de navigation minimale */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", alignItems: "center", marginBottom: "10px" }}>
            {node.allowHtml !== false && docHtml && (
              <button type="button" onClick={() => setView("html")} style={tabBtn(view === "html")}>{elakayAutoText("h_de54d4919edb65", "📄 Lire")}</button>
            )}
            {node.allowPdf !== false && isPdf && pdfPreviewUrl && (
              <button type="button" onClick={() => setView("pdf")} style={tabBtn(view === "pdf")}>{elakayAutoText("h_d8363fafbbb389", "📕 PDF")}</button>
            )}
            {node.allowOffice !== false && isWord && (
              <button type="button" onClick={() => setView("office")} style={tabBtn(view === "office")}>{elakayAutoText("h_651708c3a4cab2", "🟦 Word Live")}</button>
            )}
            {node.allowWord !== false && docUrl && (
              <a href={docUrl} download={docName}
                style={{ padding: "7px 14px", fontSize: "12px", fontWeight: 600, color: isPdf ? "#dc2626" : "#4f46e5", background: isPdf ? "#fff1f2" : "#f0eeff", border: `1.5px solid ${isPdf ? "#fda4af" : "#c4b5fd"}`, borderRadius: "8px", textDecoration: "none", display: "flex", alignItems: "center", gap: "5px" }}>
                ⬇ {docExt}
              </a>
            )}
            {node.requireSign && sigs.length > 0 && (
              <span style={{ marginLeft: "auto", fontSize: "11px", fontWeight: 700, padding: "5px 12px", borderRadius: "20px", display: "flex", alignItems: "center", gap: "5px", color: allRequiredSigned ? "#16a34a" : "#d97706", background: allRequiredSigned ? "#dcfce7" : "#fef3c7" }}>
                {allRequiredSigned ? <><Check size={11} />{elakayAutoText("h_490ef46c238acc", "Signé")}</> : `${signedCount}/${requiredCount} signé${signedCount > 1 ? "s" : ""}`}
              </span>
            )}
          </div>
          {view === "html" && totalDocPages > 0 && (
            <div style={{ marginBottom: "10px", display: "inline-flex", alignItems: "center", gap: "6px", padding: "5px 10px", borderRadius: "999px", border: "1px solid #dbeafe", background: "#eff6ff", color: "#1d4ed8", fontSize: "11px", fontWeight: 700 }}>{elakayAutoText("h_b5d8841a3b6031", "📑 Pages:")}{pageDisplayMode === "all" ? `Toutes (${totalDocPages})` : `${shownPagesCount}/${totalDocPages}`}
            </div>
          )}

          {/* Contenu direct — pas de card, le document coule dans la page */}
          <div style={{ position: "relative" }}>
            {!hasDoc ? (
              <p style={{ color: "#94a3b8", fontSize: "14px", textAlign: "center", padding: "32px 0" }}>{elakayAutoText("h_3f3085efd23a25", "Aucun document attaché.")}</p>
            ) : view === "html" && docHtml ? (
              renderDocHtml(htmlInlinePaneStyle)
            ) : view === "pdf" && pdfPreviewUrl ? (
              <iframe title={elakayAutoText("h_d613d88cb2d86f", "PDF")} src={pdfPreviewUrl}
                sandbox="" referrerPolicy="no-referrer"
                style={{ width: "100%", height: fullPageMode ? "94vh" : "85vh", border: "none", borderRadius: "8px", display: "block", ...lockedStyle }} />
            ) : view === "office" && officeEmbedUrl ? (
              <iframe title={elakayAutoText("h_71a92066ac9944", "Word Live")} src={officeEmbedUrl}
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups" referrerPolicy="no-referrer"
                style={{ width: "100%", height: fullPageMode ? "94vh" : "85vh", border: "none", borderRadius: "8px", display: "block", ...lockedStyle }} />
            ) : docUrl ? (
              <div style={{ textAlign: "center", padding: "32px 0", ...lockedStyle }}>
                <a href={docUrl} download={docName}
                  style={{ padding: "12px 24px", fontSize: "14px", fontWeight: 700, color: "#fff", background: isPdf ? "linear-gradient(135deg,#dc2626,#b91c1c)" : "linear-gradient(135deg,#2b5797,#4f46e5)", borderRadius: "10px", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: "8px", boxShadow: "0 2px 10px rgba(0,0,0,0.2)" }}>{elakayAutoText("h_2ced3c0a44d71e", "⬇ Télécharger")}{docExt}
                </a>
              </div>
            ) : null}

            {/* Overlay verrouillage */}
            {needsBlocking && hasDoc && (
              <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none", background: "rgba(255,255,255,0.4)", backdropFilter: "blur(2px)" }}>
                <div style={{ padding: "14px 24px", background: "rgba(255,255,255,0.97)", border: "1.5px solid #fcd34d", borderRadius: "14px", fontSize: "13px", fontWeight: 700, color: "#92400e", boxShadow: "0 8px 32px rgba(0,0,0,0.12)", display: "flex", alignItems: "center", gap: "8px" }}>{elakayAutoText("h_c649a931d694b9", "🔒 Signez le document pour accéder au contenu")}</div>
              </div>
            )}

            {/* Signatures apposées aux positions */}
            {sigs.map((sig, i) => {
              const sd = sigData[i];
              if (!sd?.signed) return null;
              return (
                <div key={sig.id} style={{ ...getSignPosStyleXY(sig.x ?? 75, sig.y ?? 85, sig.unit || "%"), pointerEvents: "none" }}>
                  <div style={{ border: "1.5px solid #4f46e5", borderRadius: "10px", padding: "10px 14px", background: "rgba(255,255,255,0.97)", boxShadow: "0 4px 16px rgba(79,70,229,0.2)", textAlign: "center" }}>
                    {signMode === "draw" && sd.image
                      ? <ElakayImage preset="avatar" src={sd.image} alt={elakayAutoText("h_2f32be1dc74166", "Signature")} style={{ maxWidth: 150, maxHeight: 55, display: "block", margin: "0 auto 4px" }} />
                      : <div style={{ fontSize: "20px", fontFamily: "'Brush Script MT',cursive", color: "#1e293b", lineHeight: 1.3, marginBottom: "4px" }}>{sd.name}</div>
                    }
                    {sd.date && <div style={{ fontSize: "9px", color: "#94a3b8", marginBottom: "3px" }}>{sd.date}</div>}
                    <div style={{ fontSize: "9px", color: "#16a34a", fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", gap: "3px" }}>
                      <Check size={9} /> {sig.label || elakayAutoText("h_490ef46c238acc", "Signé")}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

      ) : (

        /* ── MODE CARD (existant) ──────────────────────────────── */
        <>
          {/* Barre d'onglets */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", alignItems: "center" }}>
            {node.allowHtml !== false && docHtml && (
              <button type="button" onClick={() => setView("html")} style={tabBtn(view === "html")}>{elakayAutoText("h_de54d4919edb65", "📄 Lire")}</button>
            )}
            {node.allowOffice !== false && isWord && (
              <button type="button" onClick={() => setView("office")} style={tabBtn(view === "office")}>{elakayAutoText("h_651708c3a4cab2", "🟦 Word Live")}</button>
            )}
            {node.allowPdf !== false && isPdf && pdfPreviewUrl && (
              <button type="button" onClick={() => setView("pdf")} style={tabBtn(view === "pdf")}>{elakayAutoText("h_d8363fafbbb389", "📕 PDF")}</button>
            )}
            {node.allowWord !== false && docUrl && (
              <button type="button" onClick={() => setView("word")} style={tabBtn(view === "word")}>📥 {docExt}</button>
            )}
            {node.requireSign && sigs.length > 0 && (
              <span style={{ marginLeft: "auto", fontSize: "11px", fontWeight: 700, padding: "5px 14px", borderRadius: "20px", display: "flex", alignItems: "center", gap: "5px", color: allRequiredSigned ? "#16a34a" : "#d97706", background: allRequiredSigned ? "#dcfce7" : "#fef3c7" }}>
                {allRequiredSigned ? <><Check size={11} />{elakayAutoText("h_ff1de9912ad46c", "Tout signé")}</> : `${signedCount} / ${requiredCount} signé${signedCount > 1 ? "s" : ""}`}
              </span>
            )}
            {view === "html" && totalDocPages > 0 && (
              <span style={{ fontSize: "11px", fontWeight: 700, padding: "5px 14px", borderRadius: "20px", display: "flex", alignItems: "center", gap: "5px", color: "#1d4ed8", background: "#eff6ff", border: "1px solid #dbeafe" }}>
                📑 {pageDisplayMode === "all" ? `Toutes (${totalDocPages})` : `${shownPagesCount}/${totalDocPages}`}
              </span>
            )}
          </div>

          {/* Visualiseur card */}
          <div style={{ background: fullPageMode ? "transparent" : "#e8e8ed", borderRadius: fullPageMode ? 0 : "14px", padding: fullPageMode ? "0" : "10px", boxShadow: fullPageMode ? "none" : "inset 0 2px 10px rgba(0,0,0,0.08)" }}>
            <div style={{ background: "#fff", borderRadius: fullPageMode ? 0 : "10px", boxShadow: fullPageMode ? "0 1px 4px rgba(0,0,0,0.06)" : "0 6px 32px rgba(0,0,0,0.13),0 1px 4px rgba(0,0,0,0.07)", overflow: "hidden" }}>

              {/* En-tête */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 28px", background: "linear-gradient(135deg,#4f46e5 0%,#7c3aed 100%)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <FileText size={18} color="rgba(255,255,255,0.9)" />
                  <span style={{ color: "#fff", fontWeight: 700, fontSize: "14px", letterSpacing: "0.03em" }}>{docName}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  {docUrl && (
                    <a href={docUrl} download={docName} onClick={(e) => e.stopPropagation()}
                      style={{ fontSize: "11px", color: "rgba(255,255,255,0.9)", background: "rgba(255,255,255,0.2)", padding: "4px 12px", borderRadius: "20px", fontWeight: 600, border: "1px solid rgba(255,255,255,0.3)", textDecoration: "none", display: "flex", alignItems: "center", gap: "5px" }}>
                      ⬇ {docExt}
                    </a>
                  )}
                  {anySigned && allRequiredSigned && (
                    <span style={{ fontSize: "11px", color: "#fff", background: "rgba(34,197,94,0.3)", padding: "4px 12px", borderRadius: "20px", fontWeight: 700, border: "1px solid rgba(34,197,94,0.5)", display: "flex", alignItems: "center", gap: "5px" }}>
                      <Check size={11} />{elakayAutoText("h_490ef46c238acc", "Signé")}</span>
                  )}
                </div>
              </div>

              {/* Corps */}
              <div style={{ position: "relative" }}>
                {!hasDoc ? (
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 220, gap: "12px", padding: "48px 32px" }}>
                    <div style={{ width: 56, height: 72, background: "#f1f5f9", borderRadius: "6px", border: "2px solid #e2e8f0", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <FileText size={28} color="#cbd5e1" />
                    </div>
                    <span style={{ fontSize: "14px", color: "#94a3b8" }}>{elakayAutoText("h_3f3085efd23a25", "Aucun document attaché.")}</span>
                  </div>
                ) : view === "html" && docHtml ? (
                  renderDocHtml(htmlPaneStyle)
                ) : view === "office" ? (
                  officeEmbedUrl ? (
                    <iframe title={elakayAutoText("h_8c091c803f0a48", "Word Live Preview")} src={officeEmbedUrl}
                      sandbox="allow-scripts allow-same-origin allow-forms allow-popups" referrerPolicy="no-referrer"
                      style={{ width: "100%", height: fullPageMode ? "94vh" : "72vh", border: "none", background: "#fff", ...lockedStyle }} />
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 260, gap: "10px", padding: "24px 32px", ...lockedStyle }}>
                      <FileText size={40} color="#2b5797" />
                      <div style={{ fontWeight: 700, color: "#1e293b", fontSize: "15px" }}>{elakayAutoText("h_2492d32500fa0c", "Word Live indisponible")}</div>
                      <div style={{ fontSize: "12px", color: "#64748b", textAlign: "center", maxWidth: 520 }}>{elakayAutoText("h_428f94fe53befb", "Cette vue exige une URL publique. Utilisez la vue HTML/PDF ou le téléchargement.")}</div>
                    </div>
                  )
                ) : view === "pdf" ? (
                  pdfPreviewUrl ? (
                    <iframe title={elakayAutoText("h_2bddce78c1ce38", "PDF Preview")} src={pdfPreviewUrl}
                      sandbox="" referrerPolicy="no-referrer"
                      style={{ width: "100%", height: fullPageMode ? "94vh" : "72vh", border: "none", background: "#fff", ...lockedStyle }} />
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 260, gap: "10px", padding: "24px 32px", ...lockedStyle }}>
                      <FileText size={40} color="#dc2626" />
                      <div style={{ fontWeight: 700, color: "#1e293b", fontSize: "15px" }}>{elakayAutoText("h_788ca1e3c7ea2d", "Vue PDF indisponible")}</div>
                      <div style={{ fontSize: "12px", color: "#64748b", textAlign: "center", maxWidth: 520 }}>{elakayAutoText("h_7b7dc9d0b2eb43", "Aucun document PDF disponible.")}</div>
                    </div>
                  )
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 240, gap: "16px", padding: "40px 32px", ...lockedStyle }}>
                    <div style={{ width: 72, height: 90, borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 16px rgba(0,0,0,0.2)", background: isPdf ? "linear-gradient(135deg,#dc2626,#b91c1c)" : "linear-gradient(135deg,#2b5797,#1e3a72)" }}>
                      <FileText size={36} color="#fff" />
                    </div>
                    <div style={{ fontWeight: 700, color: "#1e293b", fontSize: "16px", textAlign: "center" }}>{docName}</div>
                    {docUrl ? (
                      <>
                        <p style={{ fontSize: "13px", color: "#64748b", textAlign: "center", maxWidth: 380, margin: 0 }}>
                          {isPdf ? elakayAutoText("h_dd1250f7d69281", "Cliquez pour ouvrir ou télécharger ce document PDF.") : elakayAutoText("h_66ac649018541e", "Cliquez pour télécharger et ouvrir dans Word ou Google Docs.")}
                        </p>
                        <a href={docUrl} download={docName}
                          style={{ padding: "11px 24px", fontSize: "14px", fontWeight: 700, color: "#fff", background: isPdf ? "linear-gradient(135deg,#dc2626,#b91c1c)" : "linear-gradient(135deg,#2b5797,#4f46e5)", borderRadius: "10px", textDecoration: "none", display: "flex", alignItems: "center", gap: "8px", boxShadow: "0 2px 10px rgba(0,0,0,0.25)" }}>{elakayAutoText("h_2ced3c0a44d71e", "⬇ Télécharger")}{docExt}
                        </a>
                      </>
                    ) : (
                      <p style={{ fontSize: "12px", color: "#94a3b8", textAlign: "center", maxWidth: 360, margin: 0 }}>{elakayAutoText("h_e50a5cef6ab415", "Fichier non disponible. Utilisez la vue HTML.")}</p>
                    )}
                  </div>
                )}

                {/* Overlay verrouillage */}
                {needsBlocking && hasDoc && (
                  <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
                    <div style={{ padding: "14px 24px", background: "rgba(255,255,255,0.97)", border: "1.5px solid #fcd34d", borderRadius: "14px", fontSize: "13px", fontWeight: 700, color: "#92400e", boxShadow: "0 8px 32px rgba(0,0,0,0.12)", display: "flex", alignItems: "center", gap: "8px" }}>{elakayAutoText("h_c649a931d694b9", "🔒 Signez le document pour accéder au contenu")}</div>
                  </div>
                )}

                {/* Signatures apposées */}
                {sigs.map((sig, i) => {
                  const sd = sigData[i];
                  if (!sd?.signed) return null;
                  return (
                    <div key={sig.id} style={{ ...getSignPosStyleXY(sig.x ?? 75, sig.y ?? 85, sig.unit || "%"), pointerEvents: "none" }}>
                      <div style={{ border: "1.5px solid #4f46e5", borderRadius: "10px", padding: "10px 14px", background: "rgba(255,255,255,0.97)", boxShadow: "0 4px 16px rgba(79,70,229,0.2)", textAlign: "center" }}>
                        {signMode === "draw" && sd.image
                          ? <ElakayImage preset="avatar" src={sd.image} alt={elakayAutoText("h_2f32be1dc74166", "Signature")} style={{ maxWidth: 150, maxHeight: 55, display: "block", margin: "0 auto 4px" }} />
                          : <div style={{ fontSize: "20px", fontFamily: "'Brush Script MT',cursive", color: "#1e293b", lineHeight: 1.3, marginBottom: "4px" }}>{sd.name}</div>
                        }
                        {sd.date && <div style={{ fontSize: "9px", color: "#94a3b8", marginBottom: "3px" }}>{sd.date}</div>}
                        <div style={{ fontSize: "9px", color: "#16a34a", fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", gap: "3px" }}>
                          <Check size={9} /> {sig.label || elakayAutoText("h_490ef46c238acc", "Signé")}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pied de page */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 28px", borderTop: "1px solid #f1f5f9", background: "#fafafa" }}>
                <span style={{ fontSize: "10px", color: "#94a3b8", fontStyle: "italic" }}>{elakayAutoText("h_b3ad63128fffd7", "Document confidentiel")}</span>
                {docUrl && (
                  <a href={docUrl} download={docName} style={{ fontSize: "10px", color: isPdf ? "#dc2626" : "#4f46e5", textDecoration: "none", fontWeight: 600 }}>{elakayAutoText("h_2ced3c0a44d71e", "⬇ Télécharger")}{docExt}
                  </a>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── Zones de signature (une par zone configurée) ── */}
      {node.allowSign && sigs.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
          {node.signMode === "both" && (
            <div style={{ display: "flex", gap: "5px" }}>
              <button type="button" onClick={() => setSignMode("draw")} style={tabBtn(signMode === "draw")}>{elakayAutoText("h_b899438c00d983", "Dessiner")}</button>
              <button type="button" onClick={() => setSignMode("type")} style={tabBtn(signMode === "type")}>{elakayAutoText("h_aa5dc005d3c6f0", "Taper")}</button>
            </div>
          )}
          {sigs.map((sig, i) => {
            const sd = sigData[i];
            if (!sd) return null;
            const setSd = (changes) => setSigData((prev) => prev.map((s, j) => j === i ? { ...s, ...changes } : s));
            return (
              <div key={sig.id} style={{ border: "1.5px solid #e0daf5", borderRadius: "14px", overflow: "hidden" }}>
                <div style={{ padding: "12px 18px", background: "linear-gradient(135deg,#f0eeff,#faf5ff)", borderBottom: "1px solid #e0daf5", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <PenLine size={15} color="#6d28d9" />
                    <strong style={{ fontSize: "13px", color: "#1e293b" }}>{sig.label || `Zone ${i + 1}`}</strong>
                    {sig.required && <span style={{ fontSize: "10px", color: "#e11d48", fontWeight: 700, background: "#fee2e2", padding: "2px 8px", borderRadius: "999px" }}>{elakayAutoText("h_389b678a374f13", "Obligatoire")}</span>}
                  </div>
                  {sd.signed && (
                    <span style={{ fontSize: "11px", color: "#16a34a", fontWeight: 700, display: "flex", alignItems: "center", gap: "4px" }}>
                      <Check size={12} />{elakayAutoText("h_490ef46c238acc", "Signé")}</span>
                  )}
                </div>
                <div style={{ padding: "16px 18px" }}>
                  {!sd.signed ? (
                    <>
                      {(node.signMode === "draw" || (node.signMode === "both" && signMode === "draw")) && (
                        <SignaturePad onChange={(img) => setSd({ image: img })} />
                      )}
                      {(node.signMode === "type" || (node.signMode === "both" && signMode === "type")) && (
                        <div style={{ display: "flex", flexDirection: "column", gap: "9px" }}>
                          <input type="text" value={sd.name} onChange={(e) => setSd({ name: e.target.value })} placeholder={elakayAutoText("h_1cf76756271089", "Votre nom complet")}
                            style={{ padding: "11px 14px", fontSize: "14px", border: "1.5px solid #e0daf5", borderRadius: "9px", outline: "none", fontFamily: "inherit" }} />
                          <input type="date" value={sd.date} onChange={(e) => setSd({ date: e.target.value })}
                            style={{ padding: "11px 14px", fontSize: "14px", border: "1.5px solid #e0daf5", borderRadius: "9px", outline: "none" }} />
                          {sd.name && (
                            <div style={{ padding: "14px 18px", fontSize: "26px", fontFamily: "'Brush Script MT',cursive", color: "#1e293b", borderTop: "1px solid #f0eeff" }}>
                              {sd.name}{sd.date ? ` — ${sd.date}` : ""}
                            </div>
                          )}
                        </div>
                      )}
                      <button type="button" onClick={() => confirmZone(i)}
                        style={{ marginTop: "12px", padding: "10px 20px", fontSize: "13px", fontWeight: 700, color: "#fff", background: "linear-gradient(135deg,#6d28d9,#4f46e5)", border: "none", borderRadius: "10px", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", boxShadow: "0 2px 10px rgba(79,70,229,0.3)" }}>
                        <PenLine size={14} />{elakayAutoText("h_ccb13a84989869", "Apposer la signature")}</button>
                    </>
                  ) : (
                    <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "12px 16px", background: "#f0fdf4", border: "1.5px solid #bbf7d0", borderRadius: "10px" }}>
                      <Check size={18} color="#16a34a" />
                      <div>
                        <div style={{ fontSize: "13px", fontWeight: 700, color: "#15803d" }}>{elakayAutoText("h_d08c66b899ff0b", "Signature apposée")}</div>
                        <div style={{ fontSize: "11px", color: "#4ade80", marginTop: "2px" }}>{elakayAutoText("h_f4d6486fe6c409", "Visible sur le document à la position configurée")}</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Aucune zone configurée */}
      {node.allowSign && sigs.length === 0 && (
        <div style={{ padding: "18px 20px", border: "1.5px solid #e0daf5", borderRadius: "12px", background: "#fafafa", textAlign: "center" }}>
          <p style={{ fontSize: "13px", color: "#94a3b8", margin: 0 }}>{elakayAutoText("h_daef7dd607a862", "Aucune zone de signature configurée pour ce document.")}</p>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Renderer principal d'un nœud, avec support de la répétition de données
// ---------------------------------------------------------------------------
// Types dont le rendu peut englober d'autres éléments interactifs internes
// (bouton de carte, liens de téléchargement, contenu imbriqué…). Les envelopper
// dans un vrai <a> produirait un <a> imbriqué dans un <a> (invalide en HTML,
// et cassé par le navigateur). wrapInLink() bascule sur un wrapper accessible
// non-anchor pour ces types-là.
const LINK_UNSAFE_NODE_TYPES = new Set([
  "modal", "dropdown", "tabs", "offcanvas", "tooltip", "accordion",
  "docUpload", "docViewer", "card", "swiper", "shopCheckout",
]);

// Shared rule: a node that has its own nested content (real children, or a
// type known to render rich interactive content on its own) must never be
// wrapped in a literal <a> by something ELSE that wants to make "the whole
// thing" clickable -- an <a> inside an <a> is invalid HTML and browsers
// silently break click/navigation on the inner one. Used by wrapInLink()
// below AND by the repeated-item "click card to view product" wrap further
// down, so both places make the exact same anchor-vs-accessible-wrapper
// decision instead of two divergent implementations.
function nodeNeedsAccessibleLinkWrapper(n) {
  return LINK_UNSAFE_NODE_TYPES.has(n?.type) || Boolean(n?.children && n.children.length > 0);
}

function RenderNodeInner({ node: rawNode, onNavigate }) {
  const dataCtx = useContext(DataContext);
  const inRouterContext = useInRouterContext();
  const device = useContext(DeviceContext);
  // Store configuration (see PageRenderer's `storeConfig` prop): a store type may rewrite or hide a
  // node before it renders (e.g. a Provider storefront hides template sample statistics).
  const transformNode = dataCtx?.storeConfig?.transformNode;
  const node = typeof transformNode === "function" ? transformNode(rawNode, dataCtx?.data) : rawNode;
  if (!node) return null;
  const sourceData = dataCtx?.data;
  const sourceKey = dataCtx?.sourceKey;
  const storeActions = dataCtx?.storeActions || {};
  const storeState = dataCtx?.storeState || {};
  const storeLinks = resolvePath(sourceData, "links") || {};

  const resolveActionArg = (raw) => {
    if (raw == null) return undefined;
    if (typeof raw !== "string") return raw;
    const value = normalizeHrefLikeValue(raw);
    if (!value) return undefined;
    if (node.__mapItem) {
      if (value === "item" || value === "items") return node.__mapItem;
      if (value.startsWith("item.") || value.startsWith("items.")) {
        const path = value.startsWith("items.") ? value.slice(6) : value.slice(5);
        const itemValue = resolvePath(node.__mapItem, path);
        if (itemValue !== undefined) return itemValue;
      }
    }
    if (sourceData && sourceKey) {
      if (value === sourceKey) return sourceData;
      if (value.startsWith(`${sourceKey}.`)) {
        return resolvePath(sourceData, value.slice(sourceKey.length + 1));
      }
    }
    if ((value.startsWith("{") && value.endsWith("}")) || (value.startsWith("[") && value.endsWith("]"))) {
      try {
        return JSON.parse(value);
      } catch (_err) {
        // keep raw value when JSON is invalid
      }
    }
    if (/^-?\d+(\.\d+)?$/.test(value)) return Number(value);
    if (value === "true") return true;
    if (value === "false") return false;
    return value;
  };

  const runClickAction = (e) => {
    if (node.toggleGroup?.id && sourceKey !== "store") {
      dataCtx?.setActiveToggle?.(node.toggleGroup.id, node.id);
    }
    const type = normalizeHrefLikeValue(node.clickActionType || "");
    if (!type || type === "none") return false;
    if (e) {
      e.preventDefault?.();
      e.stopPropagation?.();
    }

    if (type === "page") {
      const target = normalizeHrefLikeValue(node.clickActionPage || "");
      if (target && onNavigate) onNavigate(target);
      return true;
    }

    if (type === "url" || type === "href" || type === "link") {
      const resolvedUrl = resolveActionArg(node.clickActionHref);
      const url = normalizeHrefLikeValue(resolvedUrl || node.clickActionHref || "");
      if (!url) return true;
      const openBlank = !!node.clickActionTargetBlank;
      if (openBlank && typeof window !== "undefined") window.open(url, "_blank", "noopener,noreferrer");
      else if (typeof window !== "undefined") window.location.href = url;
      return true;
    }

    if (type === "function") {
      const fnName = normalizeHrefLikeValue(node.clickActionFunction || "");
      if (!fnName) return true;
      const fn = storeActions?.[fnName] || (typeof window !== "undefined" ? window.__PB_STORE_ACTIONS?.[fnName] : null);
      if (typeof fn === "function") {
        const rawArg1 = resolveActionArg(node.clickActionArg1);
        const rawArg2 = resolveActionArg(node.clickActionArg2);
        const rawArg3 = resolveActionArg(node.clickActionArg3);
        // Si arg1 n'est pas défini ET que le nœud porte un __mapItem (produit courant
        // injecté par fillNodeWithItem lors du repeatBind), on le passe automatiquement.
        // Permet à clickActionFunction:"addToCart" sur le bouton "+" d'une carte produit
        // d'ajouter le BON produit sans avoir à spécifier clickActionArg1 manuellement.
        const arg1 = rawArg1 != null ? rawArg1 : (node.__mapItem ?? rawArg1);
        const arg2 = rawArg2;
        const arg3 = rawArg3;
        fn(arg1, arg2, arg3);
      }
      return true;
    }

    return false;
  };

  // RÉPÉTITION : si le nœud a un repeatBind, on duplique sur les données
  const rb = node.repeatBind;
  if (rb && rb.source && rb.path) {
    if (sourceData && sourceKey === rb.source) {
      let arr = resolvePath(sourceData, rb.path);
      if (Array.isArray(arr) && arr.length > 0) {
        // PAGINATION locale : si activée, ne garder que la page courante.
        if (rb.paginate) {
          const ps = Math.max(1, rb.pageSize || 12);
          arr = arr.slice(0, ps);
        }

        // ── Action click : auto-detect depuis le path ou valeur manuelle ──
        const rbAct = rb.clickAction;
        const clickAct = (rbAct === "auto" || !rbAct)
          ? (rb.path?.includes("categories") ? "loadCategory"
             : rb.path?.includes("products")  ? "productLink"
             : null)
          : (rbAct === "none" ? null : rbAct);

        // ── Base URL pour les liens produits ──
        const productBase = storeLinks.productDetail || "/product/preview";
        const productIdField = rb.productIdField || "_id";

        // ── Injecter href produit dans les nœuds image du template ──
        const injectProductLinks = (filledNode, productId) => {
          if (!productId) return filledNode;
          const walk = (n) => {
            if (!n) return n;
            const upd = { ...n };
            if ((n.type === "image" || n.tag === "img") && !n.linkHref) {
              upd.linkHref = withProductShop(`${productBase}/${productId}`, { _id: sourceData?.shopId });
            }
            if (Array.isArray(n.children)) upd.children = n.children.map(walk);
            return upd;
          };
          return walk(filledNode);
        };

        // ── Grille responsive autour des items répétés ──
        const rbDgArr   = node.options?.deviceGrid;
        const rbHasDG   = rbDgArr?.length > 0;
        const rbDgClass = rbHasDG ? `pb-dg-${node.id}` : null;
        const rbDgCSS   = rbHasDG ? buildDeviceGridCSS(rbDgClass, rbDgArr) : null;

        // Construire la liste des items répétés
        const mappedItems = arr.map((item, idx) => {
          const productId = (productIdField === "_id" ? (item?.publicId || item?._id) : item?.[productIdField]) || item?.id || "";
          let filled;
          if (rb.mode === "children") {
            const filledRoot = fillNodeWithItem(
              cloneNodeFresh({ ...node, children: [] }),
              item,
              rb.fieldMap,
              rb.autoFill !== false,
              sourceData,
              sourceKey
            );
            filled = {
              ...filledRoot,
              children: (node.children || []).map((c) =>
                fillNodeWithItem(cloneNodeFresh(c), item, rb.fieldMap, rb.autoFill !== false, sourceData, sourceKey)
              ),
              repeatBind: null,
            };
          } else {
            filled = fillNodeWithItem(cloneNodeFresh(node), item, rb.fieldMap, rb.autoFill !== false, sourceData, sourceKey);
            filled.repeatBind = null;
          }
          if (rb.imageProductLink && productId) filled = injectProductLinks(filled, productId);
          const renderedNode = <RenderNode node={filled} onNavigate={onNavigate} />;
          if (clickAct === "loadCategory") {
            const catId = getShopCategoryId(item);
            const catName = getShopCategoryName(item);
            return (
              <div key={`rep-${node.id}-${idx}`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); storeActions.loadProductsByArrival_?.(catId, catName); }} style={{ cursor: "pointer", flexShrink: 0 }}>
                {renderedNode}
              </div>
            );
          }
          if (clickAct === "productLink" && productId) {
            const productHref = withProductShop(`${productBase}/${productId}`, { _id: sourceData?.shopId });
            // A repeated product-card template almost always has its own
            // children (image, title, price, an "Add to cart" button/link...)
            // -- wrapping the WHOLE card in a literal <a> would nest an <a>
            // inside an <a> for any of those (validateDOMNesting warning,
            // and the inner link silently breaks in real browsers). Same
            // rule as wrapInLink() above: fall back to the identical
            // accessible non-anchor "role=link" wrapper whenever the
            // template isn't a bare leaf.
            if (nodeNeedsAccessibleLinkWrapper(filled)) {
              const go = () => { window.location.href = productHref; };
              return (
                <div
                  key={`rep-${node.id}-${idx}`}
                  role="link"
                  tabIndex={0}
                  onClick={(e) => { if (e.defaultPrevented) return; go(); }}
                  onKeyDown={(e) => { if (e.key !== "Enter" && e.key !== " ") return; e.preventDefault(); go(); }}
                  style={{ textDecoration: "none", color: "inherit", display: "contents", cursor: "pointer" }}
                >
                  {renderedNode}
                </div>
              );
            }
            return (
              <a key={`rep-${node.id}-${idx}`} href={productHref} style={{ textDecoration: "none", color: "inherit", display: "contents" }}>
                {renderedNode}
              </a>
            );
          }
          return <React.Fragment key={`rep-${node.id}-${idx}`}>{renderedNode}</React.Fragment>;
        });

        return (
          <>
            {rbDgCSS && <style>{rbDgCSS}</style>}
            {rbHasDG
              ? <div className={rbDgClass}>{mappedItems}</div>
              : mappedItems}
          </>
        );
      }
    }
  }

  // bindKey HORS RÉPÉTITION : si bindKey est absolu (commence par la racine), on remplit depuis sourceData.
  // Exception : docViewer fait sa propre résolution (il attend un objet { docName, docUrl, docHtml }, pas une string).
  if (node.bindKey && sourceData && sourceKey && node.type !== "docViewer") {
    const key = node.bindKey;
    if (key === sourceKey || key.startsWith(sourceKey + ".")) {
      const val = resolvePath(sourceData, key.slice(sourceKey.length + 1));
      if (val != null) {
        const replaced = { ...node, bindKey: null };
        if (isImageNode(node)) {
          const src = mediaString(val);
          if (!src) return null;
          replaced.src = src;
        }
        else if (node.type === "button" || node.type === "link") {
          replaced.text = String(val);
          if (typeof val === "string" && /^https?:\/\//.test(val)) replaced.href = String(val);
        }
        else if (node.type === "input" || node.type === "textarea") replaced.field = { ...(node.field || {}), value: String(val) };
        else if (node.type === "select") replaced.options = { ...(node.options || {}), choices: Array.isArray(val) ? val.map(String) : [String(val)] };
        else if (isVideoNode(node)) {
          const url = mediaString(val);
          if (!url) return null;
          replaced.videoUrl = url;
          replaced.options = { ...(node.options || {}), url };
        }
        else if (node.type === "stat") replaced._resolvedValue = String(val);
        else replaced.text = String(val);
        return <RenderNode node={replaced} onNavigate={onNavigate} />;
      }
    }
  }

  let ns = { ...effStyle(node, device), ...parseConsoleCssToReactStyle(node.consoleCss) };
  if (node.animation && node.animation !== "none") {
    if (node.animation === "custom") ns = { ...ns, animation: buildCustomAnim(node).css };
    else if (ANIMATIONS[node.animation]) ns = { ...ns, animation: ANIMATIONS[node.animation], ...(node.animDelay ? { animationDelay: node.animDelay } : {}), ...(node.animDuration ? { animationDuration: node.animDuration } : {}) };
  }
  let toggleTextStyle = null;
  let isToggleActive = null;
  if (node.toggleGroup?.id) {
    if (sourceKey === "store") {
      const selectedCategoryId = normalizeShopCategoryId(storeState.getCi);
      const itemCategoryIds = getShopCategoryIds(node.__mapItem);
      isToggleActive = node.__mapItem
        ? Boolean(selectedCategoryId && itemCategoryIds.includes(selectedCategoryId))
        : Boolean(node.toggleGroup.isDefault && !selectedCategoryId);
    } else {
      const selectedNodeId = dataCtx?.activeToggles?.[node.toggleGroup.id];
      isToggleActive = selectedNodeId
        ? selectedNodeId === node.id
        : Boolean(node.toggleGroup.isDefault);
    }
    const resolvedGroup = dataCtx?.toggleGroupStyles?.[node.toggleGroup.id];
    const stateStyle = withToggleBackgroundColor(isToggleActive
      ? resolvedGroup?.activeStyle || node.toggleGroup.activeStyle || {}
      : resolvedGroup?.inactiveStyle || node.toggleGroup.inactiveStyle || {});
    const { color, fontWeight, fontSize, ...containerStyle } = stateStyle;
    ns = { ...ns, ...containerStyle };
    // withToggleBackgroundColor() above already normalized containerStyle to
    // a single `background` -- but `ns` (the node's own base style) may
    // still carry a leftover `backgroundColor` from BEFORE this spread, which
    // the spread alone doesn't clear. Same "mix shorthand/non-shorthand"
    // warning otherwise, one level up.
    if (Object.prototype.hasOwnProperty.call(containerStyle, "background")) {
      delete ns.backgroundColor;
    }
    toggleTextStyle = {
      ...(color != null ? { color } : {}),
      ...(fontWeight != null ? { fontWeight } : {}),
      ...(fontSize != null ? { fontSize } : {}),
    };
  }

  const wrapInLink = (inner) => {
    if (node.clickActionType && node.clickActionType !== "none") {
      return (
        <span onClick={runClickAction} style={{ display: "contents", cursor: "pointer" }}>
          {inner}
        </span>
      );
    }
    if (!node.linkHref && !node.linkTarget) {
      if (!node.toggleGroup?.id || sourceKey === "store") return inner;
      return <span onClick={runClickAction} style={{ display: "contents", cursor: "pointer" }}>{inner}</span>;
    }
    const meta = resolveNodeHrefTarget(node.linkHref, node.linkTarget);
    const handle = (e) => {
      if (runClickAction(e)) return;
      if (meta.internalTarget && onNavigate) {
        e.preventDefault();
        onNavigate(meta.internalTarget);
        return;
      }
      if (meta.href === "#") e.preventDefault();
    };
    // Un conteneur (children peuplés, ou type connu pour englober du contenu
    // riche) peut contenir son propre <a> ou bouton-lien — <a><...><a/></a/>
    // est invalide en HTML. On utilise alors un wrapper non-anchor accessible.
    if (nodeNeedsAccessibleLinkWrapper(node)) {
      const go = () => {
        if (meta.internalTarget && onNavigate) { onNavigate(meta.internalTarget); return; }
        if (meta.href && meta.href !== "#") {
          if (node.linkTargetBlank) window.open(meta.href, "_blank", "noopener,noreferrer");
          else window.location.href = meta.href;
        }
      };
      return (
        <div
          role="link"
          tabIndex={0}
          onClick={(e) => { if (runClickAction(e)) return; if (meta.internalTarget && onNavigate) { e.preventDefault(); onNavigate(meta.internalTarget); return; } if (meta.href === "#") e.preventDefault(); go(); }}
          onKeyDown={(e) => { if (e.key !== "Enter" && e.key !== " ") return; e.preventDefault(); if (runClickAction(e)) return; go(); }}
          style={{ display: "contents", cursor: "pointer" }}
        >
          {inner}
        </div>
      );
    }
    return (
      <a
        href={meta.href}
        target={node.linkTargetBlank && !meta.internalTarget ? "_blank" : undefined}
        rel={node.linkTargetBlank && !meta.internalTarget ? "noopener noreferrer" : undefined}
        onClick={handle}
        style={{ textDecoration: "none", color: "inherit", display: "contents" }}
      >
        {inner}
      </a>
    );
  };

  if (node.type === "swiper") {
    let swiperNode = node;
    if (node.bindSource && sourceData && sourceKey) {
      const bPath = node.bindSource.startsWith(sourceKey + ".")
        ? node.bindSource.slice(sourceKey.length + 1) : node.bindSource;
      const slideArr = resolvePath(sourceData, bPath);
      if (Array.isArray(slideArr) && slideArr.length > 0) {
        swiperNode = { ...node, bindSource: null, items: slideArr.map((s, i) => ({
          id: s._id || `ds-${i}`,
          image: s.urlImg || s.image || s.url || s.imgUrl || "",
          title: s.title || s.caption || "",
          text:  s.description || s.subtitle || "",
        })) };
      }
    }
    return wrapInLink(<Swiper node={swiperNode} />);
  }
  if (node.type === "modal" || node.isModal) return wrapInLink(<ModalBlock node={node} onNavigate={onNavigate} />);
  if (node.type === "dropdown") return wrapInLink(<DropdownBlock node={node} />);
  if (node.type === "tabs") return wrapInLink(<TabsBlock node={node} />);
  if (node.type === "offcanvas") return wrapInLink(<OffcanvasBlock node={node} onNavigate={onNavigate} />);
  if (node.type === "tooltip") return wrapInLink(<TooltipBlock node={node} />);
  if (node.type === "accordion") return wrapInLink(<AccordionBlock node={node} />);
  if (node.type === "docUpload") {
    const migratedDocViewerNode = {
      ...node,
      type: "docViewer",
      defaultView: node.defaultView || "html",
      allowHtml: node.allowHtml !== false,
      allowOffice: node.allowOffice !== false,
      allowPdf: node.allowPdf !== false,
      allowWord: node.allowWord !== false,
      allowDownload: node.allowDownload !== false,
      allowSign: !!node.allowSign,
      requireSign: !!node.requireSign,
      signMode: node.signMode || "both",
      folderLabel: node.folderLabel || "Mes documents",
      apiPath: node.apiPath || "/api/documents",
      autoName: node.autoName !== false,
      customName: node.customName || "",
      buttonText: node.buttonText || "Charger un document",
    };
    return wrapInLink(<DocViewerBlock node={migratedDocViewerNode} onSign={dataCtx?.onSign} />);
  }
  if (node.type === "docViewer") return wrapInLink(<DocViewerBlock node={node} onSign={dataCtx?.onSign} />);

  // ── Éléments riches boutique ──────────────────────────────────────────────
  if (node.type === "stat")      return wrapInLink(<StatBlock     node={node} />);
  if (node.type === "rating")    return wrapInLink(<RatingBlock   node={node} />);
  if (node.type === "progress")  return wrapInLink(<ProgressBlock node={node} />);
  if (node.type === "quote")     return wrapInLink(<QuoteBlock    node={node} />);
  if (node.type === "textGroup") return wrapInLink(<TextGroupBlock node={node} />);
  if (node.type === "card")      return wrapInLink(<CardBlock     node={node} onNavigate={onNavigate} />);

  // ── Store-type components (see PageRenderer's `storeConfig` prop) ─────────
  // The same shop elements (header, product grid, product card, categories...) render the store
  // type's own components when it provides them -- e.g. Provider storefronts render B2B product
  // cards (Buy Wholesale / Add to My Shop) instead of retail Buyer cards. `null` hides the element
  // (e.g. a retail checkout in a B2B storefront).
  const storeComponents = dataCtx?.storeConfig?.components;
  if (storeComponents && Object.prototype.hasOwnProperty.call(storeComponents, node.type)) {
    const StoreComponent = storeComponents[node.type];
    if (!StoreComponent) return null;
    return wrapInLink(
      <div style={ns} data-store-component={node.type}>
        <StoreComponent node={node} data={sourceData} options={node.options || {}} infoSource={dataCtx?.infoSource} />
      </div>
    );
  }

  // ── Composants boutique live — synchronisation automatique avec pageData ─
  if (node.type === "shopHeaderSeller") {
    const { infoSource: is } = dataCtx || {};
    if (!is) {
      return (
        <div style={{ ...ns, padding: "16px", color: "#9ca3af", fontSize: "13px" }}>{elakayAutoText("h_b0ef1f0a1005d6", "Header Shop Seller (infoSource manquant)")}</div>
      );
    }

    const o = node.options || {};
    const headerSettings = {
      shopNameColor: "#000000",
      menuColor: "#000000",
      searchTextColor: "#000000",
      modeInactiveColor: "#000000",
      ...o,
    };
    const owner = resolvePath(sourceData, "owner") || {};
    const ownerId = getShopOwnerId(sourceData);
    const shopNameRaw = owner?.shopName;
    const shopName = Array.isArray(shopNameRaw) ? (shopNameRaw[0] || "Seller") : (shopNameRaw || "Seller");

    const headerNode = (
      <div style={ns}>
        <HeaderShopSeller
          infoSource={is}
          userID={ownerId}
          shopName={shopName}
          loadindSearCh={storeActions.loadindSearCh || (() => {})}
          onFilterShop={storeActions.onFilterShop || (() => {})}
          search={storeState.search || ""}
          outOffPage={headerSettings.outOffPage !== false}
          showFilter={headerSettings.showFilter !== false}
          myShop={!!headerSettings.myShop}
          lakaySeller={!!headerSettings.lakaySeller}
          setActiveHeader={() => {}}
          pageShop={storeState.pageShop || "boutique"}
          setPageShop={storeActions.setPageShop || (() => {})}
          shopHeaderSettings={headerSettings}
        />
      </div>
    );

    return wrapInLink(
      inRouterContext
        ? headerNode
        : <MemoryRouter>{headerNode}</MemoryRouter>
    );
  }

  if (node.type === "shopVote") {
    const o = node.options || {};
    // Priorité : __mapItem (produit courant du repeatBind) → liveProductIndex → products.0
    const idx     = typeof o.liveProductIndex === "number" ? o.liveProductIndex : 0;
    const product = node.__mapItem
      || resolvePath(sourceData, `products.${idx}`)
      || resolvePath(sourceData, "products.0")
      || {};
    const stars = Number(product.Stars) || o.defaultRating || 0;
    const { token } = dataCtx || {};
    return wrapInLink(
      <div style={ns}>
        <Vote
          rating={stars}
          starSize={o.starSize || "28px"}
          productID={product._id}
          token={token}
          clickShow={!!token}
          ButtonAddComments={() => {}}
        />
      </div>
    );
  }

  if (node.type === "shopCarousel") {
    const { infoSource: is } = dataCtx || {};
    const pubCategoryProdMatrix =
      resolvePath(sourceData, "shopPubMatrix")
      || resolvePath(sourceData, "pubCategoryProdMatrix")
      || {};
    if (!is) return null;
    const o = node.options || {};
    return wrapInLink(
      <div style={ns}>
        <SwiperPubCategoryProdMatrix
          shopId={sourceData?.shopId}
          infoSource={is}
          pubCategoryProdMatrix={pubCategoryProdMatrix}
          inView={true}
          activeFollowBtn={o.activeFollowBtn !== false}
          isFollowing={!!dataCtx?.followState?.isFollowing}
          booFollow={!dataCtx?.followState?.isOwnShop}
          unFollow={!dataCtx?.followState?.isFollowing}
          ci={storeState.getCi || null}
          unFollowShop={storeActions.unFollowShop || (() => {})}
          onFollowToggle={storeActions.clickToFollowShop}
          loadProductsByArrival={storeActions.loadProductsByArrival_ || (() => {})}
          type_Animation={o.type_Animation || null}
          backgroundColor={o.backgroundColor || "#DCDCDC"}
          wm0={o.wm0 || 2}
          wm640={o.wm640 || 2}
          wm768={o.wm768 || 3}
          wm1024={o.wm1024 || 3}
          wm1200={o.wm1200 || 3}
          titleColor={o.titleColor || "#1f2937"}
          titleFontSize={o.titleFontSize || "1.75rem"}
          titleFontWeight={o.titleFontWeight || 700}
          titleFontFamily={o.titleFontFamily || "inherit"}
          titleFontStyle={o.titleFontStyle || "normal"}
          titleLetterSpacing={o.titleLetterSpacing || "-0.5px"}
          titleLineHeight={o.titleLineHeight ?? 1.2}
          titleTextTransform={o.titleTextTransform || "none"}
          titleTextAlign={o.titleTextAlign || "left"}
          titleTextShadow={o.titleTextShadow || "0 2px 4px rgba(0,0,0,0.05)"}
          headerBackgroundColor={o.headerBackgroundColor || "transparent"}
          headerPadding={o.headerPadding || null}
          headerMarginBottom={o.headerMarginBottom || null}
        />
      </div>
    );
  }

  if (node.type === "shopPrice") {
    const { infoSource: is } = dataCtx || {};
    const addLabel = is?.language?.(is, "Button", "addToCart") || "Ajouter au panier";
    const o = node.options || {};
    // Priorité : __mapItem (produit courant du repeatBind) → liveProductIndex → products.0
    const idx     = typeof o.liveProductIndex === "number" ? o.liveProductIndex : 0;
    const product = node.__mapItem
      || resolvePath(sourceData, `products.${idx}`)
      || resolvePath(sourceData, "products.0")
      || {};
    if (!product.price) return <div style={ns}><span style={{ color:"#9ca3af", fontSize:"13px" }}>{elakayAutoText("h_eaf53ac44ad4fa", "Prix non disponible")}</span></div>;
    const filtername = o.filtername || "";
    return wrapInLink(
      <div style={ns}>
        <ProductsPrice
          products={product}
          page={o.page || 0}
          showAddToCartButton={o.showAddToCartButton !== false}
          showAddToCart={(fn) => (
            <button
              type="button"
              onClick={() => storeActions.addToCart?.(product, fn || filtername)}
              style={{ border: "none", background: "#0073AA", color: "#fff", borderRadius: "8px", fontSize: "12px", fontWeight: 700, padding: "7px 10px", cursor: "pointer" }}
            >
              {addLabel}
            </button>
          )}
          tax={typeof o.tax === "number" ? o.tax : 0.1}
          tca={typeof o.tca === "number" ? o.tca : 0}
          shipping={typeof o.shipping === "number" ? o.shipping : 0.3}
          filtername={filtername}
        />
      </div>
    );
  }

  if (node.type === "shopCheckout") {
    const o       = node.options || {};
    const product = resolvePath(sourceData, "products.0") || {};
    const price   = product.price ? (product.price * (100 - (product.salePrice || 0)) / 100).toFixed(2) : null;
    const detailBase = storeLinks.productDetail || "/product/preview";
    const detailHref = product?._id ? withProductShop(`${detailBase}/${product._id}`, { _id: sourceData?.shopId }) : detailBase;
    const handleAddToCart = async (e) => {
      if (!storeActions.addToCart) return;
      e.preventDefault();
      await storeActions.addToCart(product);
    };
    const handleBuyNow = async (e) => {
      if (!storeActions.buyNow) return;
      e.preventDefault();
      await storeActions.buyNow(product);
    };
    return wrapInLink(
      <div style={ns}>
        <div style={{ display:"flex", flexDirection:"column", gap:"10px" }}>
          <a href={detailHref} onClick={handleAddToCart} style={{ padding:"13px 28px", background:"#0073AA", color:"#fff", border:"none", borderRadius:"10px", fontSize:"15px", fontWeight:700, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:"8px", textDecoration:"none" }}>
            🛒 {o.label || elakayAutoText("h_b6799d9a25e425", "Ajouter au panier")}
            {price && <span style={{ fontSize:"13px", opacity:0.85 }}>— ${price}</span>}
          </a>
          {o.showBuyNow !== false && (
            <a href={storeLinks.checkoutPage || "/cart"} onClick={handleBuyNow} style={{ padding:"13px 28px", background:"#fff", color:"#0073AA", border:"2px solid #0073AA", borderRadius:"10px", fontSize:"15px", fontWeight:700, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:"8px", textDecoration:"none" }}>
              ⚡ {o.labelBuy || elakayAutoText("h_af7dc34a918afa", "Acheter maintenant")}
            </a>
          )}
        </div>
      </div>
    );
  }

  // ── SliderPun135 — Hero slider boutique (store.sliderShop) ──────────────────
  if (node.type === "shopSlider") {
    const { infoSource: is } = dataCtx || {};
    if (!is) return <div style={{ ...ns, padding:"16px", color:"#9ca3af", fontSize:"13px" }}>{elakayAutoText("h_0cd711e92a4a1e", "Slider (infoSource manquant)")}</div>;
    const sliderShop = resolvePath(sourceData, "sliderShop") || [];
    const shopName   = resolvePath(sourceData, "owner.shopName.0") || resolvePath(sourceData, "owner.shopName") || "Boutique";
    const o = node.options || {};
    // Main Shop Slider Source (My Shop Pub): an Elakay Swiper replaces the
    // image slider (falls back to it when the swiper is unavailable).
    return wrapInLink(
      <div style={ns}>
        <ShopHeroSlider hero={resolvePath(sourceData, "sliderShopHero")} ownerId={resolvePath(sourceData, "owner._id") || null}>
          <SwiperPub1500x400
            infoSource={is}
            getpub={sliderShop}
            text2={typeof shopName === "string" ? shopName : (Array.isArray(shopName) ? shopName[0] : "Boutique")}
            shop={o.shop !== false}
            SliderPunShow={o.SliderPunShow || false}
          />
        </ShopHeroSlider>
      </div>
    );
  }

  // ── ProductGridShop — Grille produits paginée (store.products) ───────────────
  if (node.type === "shopProductGrid") {
    const { infoSource: is } = dataCtx || {};
    if (!is) return <div style={{ ...ns, padding:"16px", color:"#9ca3af", fontSize:"13px" }}>{elakayAutoText("h_68b846c55efb4e", "Grille produits (infoSource manquant)")}</div>;
    const products    = resolvePath(sourceData, "products") || [];
    const siseProduct = resolvePath(sourceData, "siseProduct") ?? resolvePath(sourceData, "siseProductAll") ?? products.length;
    const currentSkip = Number.isFinite(storeState.skip) ? storeState.skip : 0;
    const o = node.options || {};
    const limit = o.limit || 12;
    // Historical rendering, kept byte-for-byte for every element / device that
    // resolves to Shop Product Grid; the configurable block otherwise.
    const renderLegacyGrid = () => (
        <ProductGridShop
          currentShopId={sourceData?.shopId}
          loading={Boolean(storeState.loading)}
          productsByArrival={products}
          skip={currentSkip}
          limit={limit}
          siseProduct={siseProduct}
          size={Number.isFinite(storeState.size) ? storeState.size : siseProduct}
          prodCat={storeState.prodCat || o.prodCat || "Tous les Produits"}
          infoSource={is}
          laguangeElakay={laguangeElakay}
          Card={Card}
          ButtonPagination={ButtonPagination}
          loadMore={() => storeActions.loadMore?.(limit)}
          loadMore0={() => storeActions.loadMore0?.(limit)}
          elakayEmptyBox={elakayEmptyBox}
          gridAnimated={o.gridAnimated === true}
          animateCards={o.animateCards === true}
          resultAnimated={o.resultAnimated === true}
          resultStyle={o.resultStyle || "gradient"}
          resultBackground={o.resultBackground || null}
          showResultIcon={o.showResultIcon !== false}
          showResultBadge={o.showResultBadge !== false}
          showResultPageInfo={o.showResultPageInfo !== false}
          gridStyle={o.gridStyle || "minimal"}
          gridBackground={o.gridBackground || null}
          columns={o.columns || { xs: 2, sm: 2, md: 3, lg: 4 }}
          emptyCount={o.emptyCount ?? 12}
        />
    );
    return wrapInLink(
      <div data-shop-product-grid data-shop-products style={ns}>
        <ShopProductGridSwitch
          options={o}
          allSettings={is?.allSettings}
          shopDisplay={sourceData?.shop?.productDisplay}
          previewDevice={sourceData?.productDisplayPreviewDevice || null}
          renderLegacy={renderLegacyGrid}
          renderBlock={(effectiveOptions) => (
            <ShopProductGridBlock
              options={effectiveOptions}
              storeState={storeState}
              storeActions={storeActions}
              products={products}
              siseProduct={siseProduct}
              shopId={sourceData?.shopId}
              infoSource={is}
              laguangeElakay={laguangeElakay}
              Card={Card}
              ButtonPagination={ButtonPagination}
              elakayEmptyBox={elakayEmptyBox}
            />
          )}
        />
      </div>
    );
  }

  // ── SwiperCategoriesMatrix — Catégories (store.categories) ─────────────────
  if (node.type === "shopCatMatrix") {
    const { infoSource: is } = dataCtx || {};
    const o = node.options || {};
    if (!is || !sourceData) return (
      <div style={{ ...ns, padding:"16px", color:"#9ca3af", fontSize:"13px" }}>{elakayAutoText("h_c32404e41fbf94", "Catégories (données non disponibles)")}</div>
    );
    // store.categories = { shopName, categories: [...], owner }
    const categoriesMatrix = resolvePath(sourceData, "categories") || { categories: [], owner: "" };
    if (!Array.isArray(categoriesMatrix.categories) || categoriesMatrix.categories.length === 0) return (
      <div style={{ ...ns, padding:"16px", color:"#9ca3af", fontSize:"13px" }}>{elakayAutoText("h_af1070cad541fa", "Aucune catégorie disponible")}</div>
    );
    return wrapInLink(
      <div style={ns}>
        <SwiperCategoriesMatrix
          shopId={sourceData?.shopId}
          infoSource={is}
          categoriesMatrix={categoriesMatrix}
          inView={o.inView !== false}
          title={o.title || elakayAutoText("h_f2ef9fd31c91ee", "Nos Catégories")}
          type_Animation={o.type_Animation || null}
          backgroundColor={o.backgroundColor || "#DCDCDC"}
          nbrArr={o.nbrArr || 4}
          wm0={o.wm0 || 1.3}
          wm640={o.wm640 || 2}
          wm768={o.wm768 || 3}
          wm1024={o.wm1024 || 3}
          wm1200={o.wm1200 || 3}
          textFoorter={o.textFoorter || false}
          renderFullCardSquare={o.renderFullCardSquare || false}
          titleColor={o.titleColor || "#1f2937"}
          titleFontSize={o.titleFontSize || "1.75rem"}
          titleFontWeight={o.titleFontWeight || 700}
          titleFontFamily={o.titleFontFamily || "inherit"}
          titleFontStyle={o.titleFontStyle || "normal"}
          titleLetterSpacing={o.titleLetterSpacing || "-0.5px"}
          titleLineHeight={o.titleLineHeight ?? 1.2}
          titleTextTransform={o.titleTextTransform || "none"}
          titleTextAlign={o.titleTextAlign || "left"}
          titleTextShadow={o.titleTextShadow || "0 2px 4px rgba(0,0,0,0.05)"}
          headerBackgroundColor={o.headerBackgroundColor || "transparent"}
          headerPadding={o.headerPadding || null}
          headerMarginBottom={o.headerMarginBottom || null}
          loadProductsByArrival={storeActions.loadProductsByArrival_ || (() => {})}
          setSearch={storeActions.setSearch || (() => {})}
        />
      </div>
    );
  }

  // ── Carte Produit (Card.js) — affiche un produit individuel ─────────────────
  if (node.type === "shopCard") {
    const { infoSource: is } = dataCtx || {};
    if (!is) return (
      <div style={{ ...ns, padding: "16px", color: "#9ca3af", fontSize: "13px" }}>{elakayAutoText("h_d0cb2567a8fe68", "Carte produit (infoSource manquant)")}</div>
    );
    const o = node.options || {};
    const arrSrc = (o.liveArraySource || "store.products").replace(/^store\./, "");
    const idx = typeof o.liveProductIndex === "number" ? o.liveProductIndex
      : typeof o.productIndex === "number" ? o.productIndex : 0;
    // Priorité au produit injecté par repeatBind (__mapItem) — évite que toutes
    // les cartes répétées affichent le même produit (index 0).
    const product = node.__mapItem || resolvePath(sourceData, `${arrSrc}.${idx}`) || null;
    if (!product) return (
      <div style={{ ...ns, padding: "16px", color: "#9ca3af", fontSize: "13px" }}>{elakayAutoText("h_29725194194d3c", "Aucun produit disponible")}</div>
    );
    return wrapInLink(
      <div style={ns}>
        <Card
          currentShopId={sourceData?.shopId}
          product={product}
          infoSource={is}
          laguangeElakay={laguangeElakay}
          elakayEmptyBox={elakayEmptyBox}
          showAddToCartButton={o.showAddToCartButton !== false}
          owner={resolvePath(sourceData, "owner") || null}
          classCard0="col p-2"
          loadProductsByArrival={storeActions.loadProductsByArrival || (() => {})}
        />
      </div>
    );
  }

  // ── ShowImage — image produit avec vote, modal fiche produit ────────────────
  if (node.type === "showImage") {
    const { infoSource: is, token } = dataCtx || {};
    if (!is) return <div style={{ ...ns, padding:"12px", color:"#9ca3af", fontSize:"13px" }}>{elakayAutoText("h_8658d9e0f3e5a9", "ShowImage (infoSource manquant)")}</div>;
    const o = node.options || {};
    const slots = o.liveSlots || {};
    const arrSrc = (o.liveArraySource || "store.products").replace(/^store\./, "");
    const idx = typeof o.liveProductIndex === "number" ? o.liveProductIndex : 0;
    const base = `${arrSrc}.${idx}`;
    // Priorité au produit injecté par repeatBind (__mapItem)
    const product = node.__mapItem || resolvePath(sourceData, base) || {};
    const fromItem = (path) => resolvePath(product, path);
    const resolve = (key, fallbackPath) => {
      if (node.__mapItem) return fromItem(fallbackPath || key);
      return resolvePath(sourceData, (slots[key] || "").replace(/^store\./, "") || `${base}.${fallbackPath || key}`);
    };
    const urlImg = String(getProductPrimaryImage(product) || resolve("urlImg", "ProductImageUrls.0") || "");
    const productId = String(resolve("productId", "_id") || product.publicId || product._id || "");
    const rating = Number(resolve("rating", "Stars")) || 0;
    const nbrUserVote = resolve("nbrUserVote", "count_Comments") || 0;
    const filterProduct = resolve("filterProduct", "filterProduct") || [];
    const categoryId = String(resolve("categoryId", "categoriesID.0.catID") || "");
    const owner_id = String(resolve("owner_id", "") || resolvePath(sourceData, "owner._id") || "");
    if (!productId) return <div style={{ ...ns, padding:"12px", color:"#9ca3af", fontSize:"13px" }}>{elakayAutoText("h_29725194194d3c", "Aucun produit disponible")}</div>;
    const showImageNode = (
      <div style={ns}>
        <ShowImage
          infoSource={is}
          item={product}
          filterProduct={Array.isArray(filterProduct) ? filterProduct : []}
          nbrUserVote={nbrUserVote}
          productId={productId}
          categoryId={categoryId}
          token={token}
          showB={o.showB !== false}
          showShopV={o.showShopV !== false}
          showButtonUser={!!o.showButtonUser}
          urlImg={urlImg}
          rating={rating}
          owner_id={owner_id}
          edit={!!o.edit}
        />
      </div>
    );
    return wrapInLink(inRouterContext ? showImageNode : <MemoryRouter>{showImageNode}</MemoryRouter>);
  }

  // ── SwiperPubCategoryProdMatrix — Produits vedettes (store.shopPubMatrix) ─
  if (node.type === "shopPubMatrix") {
    const { infoSource: is } = dataCtx || {};
    const o = node.options || {};
    if (!is || !sourceData) return (
      <div style={{ ...ns, padding:"16px", color:"#9ca3af", fontSize:"13px" }}>{elakayAutoText("h_3460ecf2092a02", "Produits vedettes (données non disponibles)")}</div>
    );
    const pubCategoryProdMatrix =
      resolvePath(sourceData, "shopPubMatrix")
      || resolvePath(sourceData, "pubCategoryProdMatrix")
      || {};
    const pubProds = Array.isArray(pubCategoryProdMatrix.products) ? pubCategoryProdMatrix.products : [];
    if (pubProds.length === 0) return (
      <div style={{ ...ns, padding:"16px", color:"#9ca3af", fontSize:"13px" }}>{elakayAutoText("h_06c1e7b008f979", "Aucun produit vedette disponible")}</div>
    );
    return wrapInLink(
      <div style={ns}>
        <SwiperPubCategoryProdMatrix
          shopId={sourceData?.shopId}
          infoSource={is}
          pubCategoryProdMatrix={pubCategoryProdMatrix}
          inView={true}
          activeFollowBtn={o.activeFollowBtn || false}
          isFollowing={!!dataCtx?.followState?.isFollowing}
          booFollow={!dataCtx?.followState?.isOwnShop}
          unFollow={!dataCtx?.followState?.isFollowing}
          ci={storeState.getCi || null}
          unFollowShop={storeActions.unFollowShop || (() => {})}
          onFollowToggle={storeActions.clickToFollowShop}
          loadProductsByArrival={storeActions.loadProductsByArrival_ || (() => {})}
          type_Animation={o.type_Animation || null}
          backgroundColor={o.backgroundColor || "#DCDCDC"}
          wm0={o.wm0 || 2}
          wm640={o.wm640 || 2}
          wm768={o.wm768 || 3}
          wm1024={o.wm1024 || 3}
          wm1200={o.wm1200 || 3}
          titleColor={o.titleColor || "#1f2937"}
          titleFontSize={o.titleFontSize || "1.75rem"}
          titleFontWeight={o.titleFontWeight || 700}
          titleFontFamily={o.titleFontFamily || "inherit"}
          titleFontStyle={o.titleFontStyle || "normal"}
          titleLetterSpacing={o.titleLetterSpacing || "-0.5px"}
          titleLineHeight={o.titleLineHeight ?? 1.2}
          titleTextTransform={o.titleTextTransform || "none"}
          titleTextAlign={o.titleTextAlign || "left"}
          titleTextShadow={o.titleTextShadow || "0 2px 4px rgba(0,0,0,0.05)"}
          headerBackgroundColor={o.headerBackgroundColor || "transparent"}
          headerPadding={o.headerPadding || null}
          headerMarginBottom={o.headerMarginBottom || null}
        />
      </div>
    );
  }

  // ── ButtonPagination — boutons ← Précédent / Suivant → liés au storeState ──
  if (node.type === "shopButtonPagination") {
    const { infoSource: is } = dataCtx || {};
    if (!is) return (
      <div style={{ ...ns, padding: "8px", color: "#9ca3af", fontSize: "13px", textAlign: "center" }}>{elakayAutoText("h_40baeca96fc349", "Pagination (infoSource manquant)")}</div>
    );
    const o = node.options || {};
    // Taille totale : priorité au total serveur, sinon taille locale du tableau
    const totalSize = storeState?.size
      ?? storeState?.data?.siseProduct
      ?? storeState?.data?.siseProductAll
      ?? 0;
    const skip  = storeState?.skip  ?? 0;
    const limit = storeState?.limit ?? 12;
    return wrapInLink(
      <div style={ns}>
        <ButtonPagination
          size={totalSize}
          skip={skip}
          limit={limit}
          siseProduct={totalSize}
          loadMore={() => storeActions.loadMore?.()}
          loadMore0={() => storeActions.loadMore0?.()}
          infoSource={is}
          buttonClassL={o.buttonClassL   || "btn btn-primary pt-2 pb-2 pr-2 pl-2 rounded-4 float-left"}
          buttonClassLD={o.buttonClassLD || "btn pt-2 pb-2 pr-2 pl-2 rounded-4 float-left"}
          buttonClassR={o.buttonClassR   || "btn btn-primary pt-2 pb-2 pr-2 pl-2 rounded-4 float-right"}
          buttonClassRD={o.buttonClassRD || "btn pt-2 pb-2 pr-2 pl-2 rounded-4 float-right"}
        />
      </div>
    );
  }

  if (node.type === "image") {
    const src = mediaString(node.src);
    if (node._hideWhenEmptyMedia || (node.bindKey && !src)) return null;
    return wrapInLink(<ElakayImage preset="content" src={src} alt={node.alt || ""} style={ns} />);
  }
  if (node.type === "button") {
    if (node.shopFollowButton && sourceKey === "store") {
      const follow = dataCtx?.followState || {};
      if (!follow.ownerId || follow.isOwnShop) return null;
      const translateButton = (key, fallback) => {
        try {
          return dataCtx?.infoSource?.language?.(dataCtx.infoSource, "Button", key) || fallback;
        } catch (_err) {
          return fallback;
        }
      };
      const followLabel = node.followText || translateButton("follow", "Follow");
      const unfollowLabel = node.unfollowText || translateButton("unFollow", "Unfollow");
      const buttonText = follow.loading
        ? (node.loadingText || "...")
        : (follow.isFollowing ? unfollowLabel : followLabel);
      const stateStyle = follow.isFollowing
        ? (node.unfollowStyle || {})
        : (node.followStyle || {});
      return (
        <button
          type="button"
          aria-pressed={follow.isFollowing}
          aria-busy={follow.loading}
          disabled={follow.loading}
          title={follow.error || buttonText}
          onClick={(event) => runClickAction(event)}
          style={{
            ...ns,
            ...stateStyle,
            opacity: follow.loading ? 0.68 : (stateStyle.opacity ?? ns.opacity),
            cursor: follow.loading ? "wait" : (stateStyle.cursor || ns.cursor || "pointer"),
          }}
        >
          {buttonText}
        </button>
      );
    }
    const fallbackShopHref = sourceKey === "store" ? normalizeHrefLikeValue(storeLinks.shopPage) : "";
    const meta = resolveNodeHrefTarget(node.href || fallbackShopHref, node.target);
    const onClick = (e) => {
      if (runClickAction(e)) return;
      if (meta.internalTarget && onNavigate) {
        e.preventDefault();
        onNavigate(meta.internalTarget);
        return;
      }
      if (meta.href === "#") e.preventDefault();
    };
    return <a href={meta.href} onClick={onClick} style={ns}>{node.text}</a>;
  }
  if (node.type === "link") {
    const fallbackShopHref = sourceKey === "store" ? normalizeHrefLikeValue(storeLinks.shopPage) : "";
    const meta = resolveNodeHrefTarget(node.href || fallbackShopHref, node.target);
    const onClick = (e) => {
      if (runClickAction(e)) return;
      if (meta.internalTarget && onNavigate) {
        e.preventDefault();
        onNavigate(meta.internalTarget);
        return;
      }
      if (meta.href === "#") e.preventDefault();
    };
    return <a href={meta.href} onClick={onClick} style={ns}>{node.text}</a>;
  }
  if (node.type === "elakaySwiper") {
    // Swiper Manager swiper, by reference: the one renderer (<DynamicSwiper>).
    return (
      <div style={ns}>
        <ElakaySwiperBlock
          swiperId={node.options?.swiperId}
          swiperName={node.options?.swiperName}
          maxWidth={Number(node.options?.maxWidth) || 0}
          // Whose shop / page shows it: a Family swiper no longer shared with
          // this owner's role is hidden here (the block itself is kept).
          ownerId={resolvePath(sourceData, "owner._id") || null}
        />
      </div>
    );
  }
  if (node.type === "video") {
    const url = mediaString(node.videoUrl || node.src || node.options?.url || "");
    const safeUrl = safePageResourceUrl(url);
    if (node._hideWhenEmptyMedia || (node.bindKey && !safeUrl) || !safeUrl) return null;
    const isFile = /\.(mp4|webm|ogg|mov)(?:[?#]|$)/i.test(safeUrl);
    return wrapInLink(isFile
      ? <video src={toFileUrl(safeUrl)} controls style={ns} />
      : <iframe src={toEmbedUrl(safeUrl)} sandbox="allow-scripts allow-same-origin allow-presentation" referrerPolicy="no-referrer" style={ns} allow="autoplay; encrypted-media" allowFullScreen title={elakayAutoText("h_c4277d8b7ae7cc", "vidéo")} />);
  }
  if (node.type === "input" || node.type === "datalist") {
    const f = node.field || {};
    return (
      <div style={{ marginBottom: "16px" }}>
        {f.label && <label style={{ display: "block", fontSize: "13px", fontWeight: 600, color: "#4a4a68", marginBottom: "6px" }}>{f.label}</label>}
        <input type={f.inputType || "text"} placeholder={f.placeholder} name={f.name} defaultValue={f.value} required={f.required} disabled={f.disabled} readOnly={f.readonly} min={f.min} max={f.max} step={f.step} pattern={f.pattern} list={node.type === "datalist" ? `dl-${node.id}` : undefined} style={ns} />
        {node.type === "datalist" && (
          <datalist id={`dl-${node.id}`}>{(node.options?.choices || []).map((c) => <option key={c} value={c} />)}</datalist>
        )}
      </div>
    );
  }
  if (node.type === "textarea") {
    const f = node.field || {};
    return (
      <div style={{ marginBottom: "16px" }}>
        {f.label && <label style={{ display: "block", fontSize: "13px", fontWeight: 600, color: "#4a4a68", marginBottom: "6px" }}>{f.label}</label>}
        <textarea placeholder={f.placeholder} rows={f.rows || 4} name={f.name} defaultValue={f.value} required={f.required} disabled={f.disabled} readOnly={f.readonly} style={ns} />
      </div>
    );
  }
  if (node.type === "select") {
    const f = node.field || {};
    return (
      <div style={{ marginBottom: "16px" }}>
        {f.label && <label style={{ display: "block", fontSize: "13px", fontWeight: 600, color: "#4a4a68", marginBottom: "6px" }}>{f.label}</label>}
        <select name={f.name} defaultValue={f.value} required={f.required} disabled={f.disabled} style={ns}>
          {(node.options?.choices || []).map((c) => <option key={c}>{c}</option>)}
        </select>
      </div>
    );
  }
  if (node.type === "checkbox" || node.type === "radio" || node.type === "toggle") {
    const f = node.field || {};
    return (
      <label style={ns}>
        <input type={node.type === "radio" ? "radio" : "checkbox"} name={f.name} value={f.value} defaultChecked={f.checked} style={{ accentColor: "#6d28d9", width: "17px", height: "17px" }} />
        {f.label}
      </label>
    );
  }
  if (node.type === "output") return <output style={ns}>{node.content?.text}</output>;
  if (node.type === "divider") return <hr style={ns} />;

  const Tag = node.tag || "div";
  const isFieldset = node.type === "fieldset";

  // Grille CSS responsive : deviceGrid > options.columns
  const isGridType = node.type === "grid";
  const dgArr = node.options?.deviceGrid;
  const hasDG  = isGridType && dgArr?.length;
  const dgClass = hasDG ? `pb-dg-${node.id}` : null;
  const dgCSS   = hasDG ? buildDeviceGridCSS(dgClass, dgArr) : null;
  const gridStyle = isGridType
    ? (hasDG
        ? ns  // CSS handled by <style> tag below
        : node.options?.columns
          ? { ...ns, display: "grid", gridTemplateColumns: `repeat(${node.options.columns}, minmax(0,1fr))` }
          : ns)
    : ns;

  return wrapInLink(
    <>
      {dgCSS && <style>{dgCSS}</style>}
      <Tag
        className={dgClass || undefined}
        style={isGridType ? gridStyle : ns}
        data-category-active={isToggleActive == null ? undefined : String(isToggleActive)}
        {...(node.attrs || {})}
      >
        {isFieldset && node.legend && <legend style={{ fontSize: "13px", fontWeight: 700, color: "#6d28d9", padding: "0 6px" }}>{node.legend}</legend>}
        {node.text}
        {node.children?.map((c) => (
          <RenderNode
            key={c.id}
            node={toggleTextStyle && Object.keys(toggleTextStyle).length
              ? { ...c, style: { ...(c.style || {}), ...toggleTextStyle } }
              : c}
            onNavigate={onNavigate}
          />
        ))}
      </Tag>
    </>
  );
}

function RenderNode(props) {
  const { node } = props;
  // `consoleJs` remains in historical documents for round-trip compatibility,
  // but is deliberately inert. Executing stored strings with Function() in the
  // application origin lets a published page read the visitor's session. Safe
  // interactions (navigation, toggles, widgets, store actions) are implemented
  // as explicit React behavior elsewhere in this renderer.
  return (
    <div data-pb-node-id={node?.id} style={{ display: "contents" }}>
      <RenderNodeInner {...props} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Composant principal exporté
// ---------------------------------------------------------------------------
// `storeConfig` (optional, store pages only) lets a store type reuse this renderer unchanged:
//   components     { [shopElementType]: Component | null } -- replaces/hides shop elements
//   actions        overrides of the store actions (viewProduct, addToCart, clickToFollowShop...)
//   transformNode  (node, data) => node | null, applied to every node before rendering
//   localProducts  true = product paging/search/categories run on pageData.products only (never
//                  the Seller shop API)
//   disableFollowBar  true = no runtime "follow this shop" bar injected into shop templates
//   hidePageNavigation true = no built-in page switcher (the host renders its own navigation and
//                  drives the page through `pageId` / `onNavigate`)
// Without it, rendering is exactly the Seller/Buyer behaviour.
export default function PageRenderer({ doc, pageData, pageId, device = "desktop", onNavigate, onSign, infoSource = null, token = null, htmlSource = null, htmlSourcePages = null, storeConfig = null }) {
  const [activeId, setActiveId] = useState(pageId || doc?.currentPageId || doc?.pages?.[0]?.id);
  const [activeToggles, setActiveToggles] = useState({});
  const setActiveToggle = useCallback((groupId, nodeId) => {
    setActiveToggles((current) => ({ ...current, [groupId]: nodeId }));
  }, []);

  // Synchroniser l'id actif quand pageId change (navigation depuis l'aperçu du PageBuilder)
  useEffect(() => {
    if (pageId) setActiveId(pageId);
  }, [pageId]);

  // Réinitialiser la page active quand le doc change (nouveau template chargé). Une page demandée
  // explicitement via `pageId` reste prioritaire : sans cela, au montage, cet effet (exécuté après
  // celui de `pageId`) ramenait toujours l'affichage sur la première page.
  useEffect(() => {
    setActiveId(pageId || doc?.currentPageId || doc?.pages?.[0]?.id);
  }, [doc]); // eslint-disable-line react-hooks/exhaustive-deps -- pageId has its own effect above

  // Clé de source pour les bindings (déduite du pageMode)
  const sourceKey = doc?.pageMode || doc?.meta?.pageMode || "store";
  const pageRootRef = useRef(null);

  const buildInitialStoreState = useMemo(() => (rawData) => {
    const srcBase = rawData && typeof rawData === "object" ? rawData : {};
    const src = {
      ...srcBase,
      shopPubMatrix: srcBase.shopPubMatrix || srcBase.pubCategoryProdMatrix || { shopName: "", products: [] },
    };
    const allProducts = Array.isArray(src.products) ? src.products : [];
    const limit = 12;
    const firstPage = allProducts.slice(0, limit);
    return {
      data: { ...src, products: firstPage, siseProduct: src.siseProduct ?? allProducts.length, size: src.size ?? allProducts.length },
      allProducts,
      skip: 0,
      limit,
      size: src.size ?? allProducts.length,
      getCi: null,
      search: "",
      prodCat: "All Products",
      myFilters: { filters: {} },
      // Product order of the shop grid (Shop Builder "Product Grid" sort).
      sort: DEFAULT_SHOP_SORT,
      pageShop: "boutique",
      remoteMode: false,
      loading: false,
    };
  }, []);

  const [storeState, setStoreState] = useState(() =>
    sourceKey === "store" ? buildInitialStoreState(pageData) : null
  );
  const storeStateRef = useRef(storeState);
  const shopRequestVersion = useRef(0);
  const storeConfigRef = useRef(storeConfig);
  storeConfigRef.current = storeConfig;

  useEffect(() => {
    storeStateRef.current = storeState;
  }, [storeState]);

  // The state above is already built from the first pageData: rebuilding it
  // again on mount would also invalidate (shopRequestVersion) the first-page
  // request a child block (Shop Builder product grid) sends from its own
  // mount effect, which runs before this one.
  const storeBuiltFrom = useRef(sourceKey === "store" ? pageData : undefined);
  useEffect(() => {
    if (sourceKey !== "store") {
      storeBuiltFrom.current = undefined;
      setStoreState(null);
      return;
    }
    if (storeBuiltFrom.current === pageData) return;
    storeBuiltFrom.current = pageData;
    shopRequestVersion.current++;
    setStoreState(buildInitialStoreState(pageData));
  }, [sourceKey, pageData, buildInitialStoreState]);

  const authSession = isAuthenticated() || {};
  const currentUserId = normalizeShopUserId(
    infoSource?.userInfo?._id ??
    authSession?.user?._id ??
    authSession?._id
  );
  const adminUserIds = getAdminUserIds(infoSource?.userAdmin);
  const adminUserIdsKey = adminUserIds.join("|");
  const storeOwnerId = normalizeShopUserId(getShopOwnerId(storeState?.data || pageData));
  const storeShopId = storeState?.data?.shopId || pageData?.shopId || null;
  const followList =
    infoSource?.getShopFollow ??
    infoSource?.userInfo?.followShopUser ??
    authSession?.user?.followShopUser ??
    [];
  const followListKey = (Array.isArray(followList) ? followList : [])
    .map((entry) => `${getFollowEntryId(entry)}:${entry?.shopId || ""}`)
    .filter(Boolean)
    .join("|");
  const [followState, setFollowState] = useState({
    ownerId: null,
    isFollowing: false,
    isOwnShop: false,
    loading: false,
    error: "",
  });

  useEffect(() => {
    if (sourceKey !== "store") return;
    const isOwnShop = Boolean(
      storeOwnerId &&
      (storeOwnerId === currentUserId || adminUserIds.includes(storeOwnerId))
    );
    setFollowState((prev) => ({
      ...prev,
      ownerId: storeOwnerId,
      isFollowing: isShopFollowed(followList, storeShopId),
      isOwnShop,
      loading: prev.ownerId === storeOwnerId ? prev.loading : false,
      error: "",
    }));
  }, [sourceKey, storeOwnerId, storeShopId, currentUserId, adminUserIdsKey, followListKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const scrollStoreTop = useCallback(() => {
    pageRootRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const applyLocalStoreView = useCallback((patch = {}) => {
    setStoreState((prev) => {
      if (!prev) return prev;
      const limit = Math.max(1, Number(patch.limit ?? prev.limit ?? 12));
      const skip = Math.max(0, Number(patch.skip ?? prev.skip ?? 0));
      const search = patch.search !== undefined ? patch.search : prev.search;
      const getCi = patch.getCi !== undefined ? patch.getCi : prev.getCi;
      const base = Array.isArray(prev.allProducts) ? prev.allProducts : [];
      const categoryMatches = getCi ? base.filter((p) => matchesShopCategory(p, getCi)) : base;
      const selectedCategory = getCi
        ? getStoreCategories(prev.data).find((category) => getShopCategoryIds(category).includes(normalizeShopCategoryId(getCi)))
        : null;
      const embeddedProducts = Array.isArray(selectedCategory?.products) ? selectedCategory.products : [];
      const categoryProducts = getCi && categoryMatches.length === 0 && embeddedProducts.length > 0
        ? embeddedProducts
        : categoryMatches;
      const sort = patch.sort || prev.sort || DEFAULT_SHOP_SORT;
      const filtered = sortShopProductsLocally(categoryProducts
        .filter((p) => matchesShopSearch(p, search)), sort);
      const visible = filtered.slice(skip, skip + limit);
      const nextData = {
        ...(prev.data || {}),
        products: visible,
        siseProduct: filtered.length,
        size: filtered.length,
      };
      return {
        ...prev,
        data: nextData,
        skip,
        limit,
        size: filtered.length,
        search,
        getCi,
        prodCat: patch.prodCat !== undefined ? patch.prodCat : prev.prodCat,
        myFilters: patch.myFilters || prev.myFilters,
        sort,
        remoteMode: false,
        loading: false,
      };
    });
  }, []);

  const applyRemoteStoreResponse = useCallback((response, patch = {}) => {
    setStoreState((prev) => {
      if (!prev) return prev;
      const nextPub = response?.shopPubMatrix || response?.pubCategoryProdMatrix || null;
      const nextData = {
        ...(prev.data || {}),
        ...(response?.owner ? { owner: response.owner } : {}),
        ...(response?.categories ? { categories: response.categories } : {}),
        ...(response?.pubCategoryProdMatrix ? { pubCategoryProdMatrix: response.pubCategoryProdMatrix } : {}),
        ...(nextPub ? { shopPubMatrix: nextPub } : {}),
        ...(response?.filters ? { filters: response.filters } : {}),
        ...(Array.isArray(response?.products) ? { products: response.products } : {}),
      };
      const nextSiseProduct =
        response?.siseProduct ??
        response?.siseProductAll ??
        nextData?.siseProduct ??
        (Array.isArray(nextData.products) ? nextData.products.length : 0);
      const nextSize =
        response?.size ??
        response?.siseProductAll ??
        response?.siseProduct ??
        nextData?.size ??
        nextSiseProduct;
      nextData.siseProduct = nextSiseProduct;
      nextData.siseProductAll = response?.siseProductAll ?? nextData?.siseProductAll ?? nextSize;
      nextData.size = nextSize;
      return {
        ...prev,
        data: nextData,
        skip: patch.skip ?? prev.skip,
        limit: patch.limit ?? prev.limit,
        size: nextSize,
        getCi: patch.getCi !== undefined ? patch.getCi : prev.getCi,
        search: patch.search !== undefined ? patch.search : prev.search,
        prodCat: patch.prodCat !== undefined ? patch.prodCat : prev.prodCat,
        myFilters: patch.myFilters || prev.myFilters,
        sort: patch.sort || prev.sort || DEFAULT_SHOP_SORT,
        remoteMode: true,
        loading: false,
      };
    });
  }, []);

  const fetchShopProducts = useCallback(async ({
    skip = 0,
    limit,
    getCi = null,
    search = "",
    myFilters = null,
    facet = null,
    prodCat,
    sort: sortArg = null,
  } = {}) => {
    if (sourceKey !== "store") return;
    const current = storeStateRef.current;
    if (!current) return;
    const sort = normalizeShopSort(sortArg || current.sort);
    const useLimit = Math.max(1, Number(limit ?? current.limit ?? 12));
    const safeSkip = Math.max(0, Number(skip || 0));
    const filters = toSafeFilters(myFilters || current.myFilters);
    const ownerId = storeConfigRef.current?.localProducts ? null : getShopOwnerId(current.data);
    const requestVersion = ++shopRequestVersion.current;

    setStoreState((prev) => (prev ? {
      ...prev,
      loading: true,
      skip: safeSkip,
      limit: useLimit,
      getCi,
      search,
      sort,
      prodCat: prodCat !== undefined ? prodCat : prev.prodCat,
    } : prev));

    if (!ownerId) {
      applyLocalStoreView({
        skip: safeSkip,
        limit: useLimit,
        getCi,
        search,
        myFilters: filters,
        prodCat,
        sort,
      });
      return;
    }

    try {
      const response = await getLakayUser(
        ownerId,
        safeSkip,
        useLimit,
        sort.sortBy,
        getCi,
        search,
        facet ? { ...filters, facet } : filters,
        undefined,
        current.data?.shopId || null,
        // Only an ascending order adds the options argument (newest first
        // keeps the historical 9-argument call).
        ...(sort.asc ? [{ asc: true }] : [])
      );
      if (requestVersion !== shopRequestVersion.current) return;
      if (!response || response.error) {
        applyLocalStoreView({
          skip: safeSkip,
          limit: useLimit,
          getCi,
          search,
          myFilters: filters,
          prodCat,
        });
        return;
      }
      applyRemoteStoreResponse(response, {
        skip: safeSkip,
        limit: useLimit,
        getCi,
        search,
        myFilters: filters,
        prodCat,
        sort,
      });
    } catch (_err) {
      if (requestVersion !== shopRequestVersion.current) return;
      applyLocalStoreView({
        skip: safeSkip,
        limit: useLimit,
        getCi,
        search,
        myFilters: filters,
        prodCat,
        sort,
      });
    }
  }, [applyLocalStoreView, applyRemoteStoreResponse, sourceKey]);

  const loadProductsByArrival = useCallback(async (searchArg = null, myFiltersArg = null, forcedLimit = null) => {
    const current = storeStateRef.current;
    if (!current) return;
    await fetchShopProducts({
      skip: 0,
      limit: forcedLimit ?? current.limit,
      getCi: null,
      search: searchArg == null ? current.search : String(searchArg),
      myFilters: myFiltersArg || current.myFilters,
      facet: ["shopLakayUser", "shop_Owner", "shop_Filters", "shop_Products_Categories", "pubCategoryProd"],
      prodCat: "All Products",
    });
    scrollStoreTop();
  }, [fetchShopProducts, scrollStoreTop]);

  const loadProductsByArrival_ = useCallback(async (categoryId = null, categoryName = "All Products", forcedLimit = null) => {
    const current = storeStateRef.current;
    if (!current) return;
    await fetchShopProducts({
      skip: 0,
      limit: forcedLimit ?? current.limit,
      getCi: categoryId,
      search: "",
      myFilters: current.myFilters,
      facet: ["shopLakayUser", "shop_Filters"],
      prodCat: categoryName || "All Products",
    });
    scrollStoreTop();
  }, [fetchShopProducts, scrollStoreTop]);

  const loadindSearCh = useCallback(async (searchValue = "") => {
    const current = storeStateRef.current;
    if (!current) return;
    await fetchShopProducts({
      skip: 0,
      limit: current.limit,
      getCi: current.getCi,
      search: String(searchValue || ""),
      myFilters: current.myFilters,
      facet: ["shopLakayUser", "shop_Filters"],
      prodCat: current.prodCat,
    });
    scrollStoreTop();
  }, [fetchShopProducts, scrollStoreTop]);

  const loadMore = useCallback(async (forcedLimit = null) => {
    const current = storeStateRef.current;
    if (!current) return;
    const limit = Math.max(1, Number(forcedLimit ?? current.limit ?? 12));
    await fetchShopProducts({
      skip: (current.skip || 0) + limit,
      limit,
      getCi: current.getCi,
      search: current.search,
      myFilters: current.myFilters,
      facet: ["shopLakayUser"],
      prodCat: current.prodCat,
    });
    scrollStoreTop();
  }, [fetchShopProducts, scrollStoreTop]);

  const loadMore0 = useCallback(async (forcedLimit = null) => {
    const current = storeStateRef.current;
    if (!current) return;
    const limit = Math.max(1, Number(forcedLimit ?? current.limit ?? 12));
    await fetchShopProducts({
      skip: Math.max(0, (current.skip || 0) - limit),
      limit,
      getCi: current.getCi,
      search: current.search,
      myFilters: current.myFilters,
      facet: ["shopLakayUser"],
      prodCat: current.prodCat,
    });
    scrollStoreTop();
  }, [fetchShopProducts, scrollStoreTop]);

  const onFilterShop = useCallback(async () => {
    const current = storeStateRef.current;
    if (!current) return;
    await loadProductsByArrival("", current.myFilters, current.limit);
  }, [loadProductsByArrival]);

  const setFilterShop = useCallback((value) => {
    setStoreState((prev) => (prev ? { ...prev, filterShop: !!value } : prev));
  }, []);

  const setSearch = useCallback((value) => {
    setStoreState((prev) => (prev ? { ...prev, search: String(value || "") } : prev));
  }, []);

  const setPageShop = useCallback((value) => {
    setStoreState((prev) => (prev ? { ...prev, pageShop: value || "boutique" } : prev));
  }, []);

  const scrollToProducts = useCallback(() => {
    const target = pageRootRef.current?.querySelector(
      "[data-shop-product-grid], [data-shop-paginated-grid], [data-shop-products]"
    );
    (target || pageRootRef.current)?.scrollIntoView?.({ behavior: "smooth", block: "start" });
    return Boolean(target);
  }, []);

  const clearSearch = useCallback(async () => {
    setSearch("");
    return loadindSearCh("");
  }, [loadindSearCh, setSearch]);

  const resetShopFilters = useCallback(async () => {
    const current = storeStateRef.current;
    if (!current) return null;
    setFilterShop(false);
    return loadProductsByArrival("", { filters: {} }, current.limit);
  }, [loadProductsByArrival, setFilterShop]);

  const loadProducts = useCallback(async (params = {}) => {
    const current = storeStateRef.current;
    if (!current) return null;
    return fetchShopProducts({
      skip: params?.skip ?? 0,
      limit: params?.limit ?? current.limit,
      getCi: params?.categoryId ?? params?.getCi ?? current.getCi,
      search: params?.search ?? current.search,
      myFilters: params?.filters ?? current.myFilters,
      facet: params?.facet ?? ["shopLakayUser", "shop_Filters"],
      prodCat: params?.categoryName ?? params?.prodCat ?? current.prodCat,
      sort: params?.sort ?? current.sort,
    });
  }, [fetchShopProducts]);

  // Infinite scroll of the Shop Builder product grid: the next batch is
  // APPENDED (deduplicated by _id) with skip = products already shown, so
  // nothing is skipped or repeated. One request at a time; a batch that
  // answers after the filters/category/sort changed is dropped.
  const appendInFlight = useRef(false);
  const appendShopProducts = useCallback(async ({ limit } = {}) => {
    if (sourceKey !== "store") return { ok: false, error: "no-store" };
    const current = storeStateRef.current;
    if (!current || appendInFlight.current) return { ok: false, busy: true };
    const batch = Math.min(50, Math.max(1, Number(limit) || current.limit || 12));
    const shown = Array.isArray(current.data?.products) ? current.data.products : [];
    const sort = normalizeShopSort(current.sort);
    const ownerId = storeConfigRef.current?.localProducts ? null : getShopOwnerId(current.data);
    const version = shopRequestVersion.current;
    if (!ownerId) {
      // Builder preview on local sample data: reveal the next local slice.
      applyLocalStoreView({ skip: 0, limit: shown.length + batch, sort });
      return { ok: true, received: batch };
    }
    appendInFlight.current = true;
    setStoreState((prev) => (prev ? { ...prev, appending: true } : prev));
    try {
      const response = await getLakayUser(
        ownerId,
        shown.length,
        batch,
        sort.sortBy,
        current.getCi,
        current.search,
        { ...toSafeFilters(current.myFilters), facet: ["shopLakayUser"] },
        undefined,
        current.data?.shopId || null,
        // Only an ascending order adds the options argument (newest first
        // keeps the historical 9-argument call).
        ...(sort.asc ? [{ asc: true }] : [])
      );
      if (version !== shopRequestVersion.current) {
        setStoreState((prev) => (prev ? { ...prev, appending: false } : prev));
        return { ok: false, stale: true };
      }
      if (!response || response.error) throw new Error(response?.error || "load-failed");
      const received = Array.isArray(response.products) ? response.products : [];
      setStoreState((prev) => {
        if (!prev) return prev;
        const existing = Array.isArray(prev.data?.products) ? prev.data.products : [];
        const seen = new Set(existing.map((product) => String(product?._id || product?.publicId || "")));
        const merged = [...existing, ...received.filter((product) => {
          const key = String(product?._id || product?.publicId || "");
          if (!key || seen.has(key)) return false;
          seen.add(key);
          return true;
        })];
        const total = response.siseProduct ?? prev.data?.siseProduct ?? merged.length;
        return {
          ...prev,
          appending: false,
          data: { ...(prev.data || {}), products: merged, siseProduct: total, size: merged.length },
          size: merged.length,
        };
      });
      return { ok: true, received: received.length };
    } catch (error) {
      setStoreState((prev) => (prev ? { ...prev, appending: false } : prev));
      return { ok: false, error: error?.message || "load-failed" };
    } finally {
      appendInFlight.current = false;
    }
  }, [sourceKey, applyLocalStoreView]);

  const navigateStoreLink = useCallback((linkName, fallback, product = null) => {
    const current = storeStateRef.current;
    const links = current?.data?.links || {};
    let href = normalizeHrefLikeValue(links[linkName] || fallback);
    const productId = normalizeShopUserId(product?.publicId ?? product?._id ?? product?.id ?? product);
    if (productId && linkName === "productDetail") {
      href = withProductShop(`${href.replace(/\/$/, "")}/${encodeURIComponent(productId)}`, { _id: current?.data?.shopId });
    }
    if (href && href !== "#" && typeof window !== "undefined") window.location.href = href;
    return href;
  }, []);

  const viewProduct = useCallback(
    (product) => navigateStoreLink("productDetail", "/product/preview", product),
    [navigateStoreLink]
  );
  const goToCart = useCallback(
    () => navigateStoreLink("cartPage", "/cart"),
    [navigateStoreLink]
  );
  const goToCheckout = useCallback(
    () => navigateStoreLink("checkoutPage", "/cart"),
    [navigateStoreLink]
  );
  const goToShop = useCallback(
    () => navigateStoreLink("shopPage", "#"),
    [navigateStoreLink]
  );

  const findToFollowU = useCallback((_followEntries, userAdmin, userId) => {
    const normalizedUserId = normalizeShopUserId(userId);
    const adminIds = getAdminUserIds(userAdmin);
    const isOwnShop = Boolean(
      storeOwnerId &&
      (storeOwnerId === normalizedUserId || adminIds.includes(storeOwnerId))
    );
    setFollowState((prev) => ({ ...prev, ownerId: storeOwnerId, isOwnShop }));
    return !isOwnShop;
  }, [storeOwnerId]);

  const unFollowShop = useCallback((entries = []) => {
    const isFollowing = isShopFollowed(entries, storeShopId);
    setFollowState((prev) => ({
      ...prev,
      ownerId: storeOwnerId,
      isFollowing,
      loading: false,
      error: "",
    }));
    return !isFollowing;
  }, [storeOwnerId, storeShopId]);

  const clickToFollowShop = useCallback(async (ownerArg = null) => {
    const ownerId = normalizeShopUserId(ownerArg) || storeOwnerId;
    if (!ownerId) {
      const result = { success: false, error: elakayAutoText("h_e9e68bcb88751a", "Boutique introuvable.") };
      setFollowState((prev) => ({ ...prev, loading: false, error: result.error }));
      return result;
    }

    const session = isAuthenticated() || {};
    const authToken = token || infoSource?.token || session?.token;
    const userId = normalizeShopUserId(infoSource?.userInfo?._id ?? session?.user?._id ?? session?._id);
    const adminIds = getAdminUserIds(infoSource?.userAdmin);
    if (ownerId === userId || adminIds.includes(ownerId)) {
      const result = { success: false, error: elakayAutoText("h_08ac0a8c7215c4", "Vous ne pouvez pas suivre votre propre boutique.") };
      setFollowState((prev) => ({ ...prev, ownerId, isOwnShop: true, loading: false, error: result.error }));
      return result;
    }

    if (!authToken) {
      if (typeof window !== "undefined") window.location.href = "/signin";
      return { success: false, error: "not-authenticated" };
    }

    setFollowState((prev) => ({ ...prev, ownerId, loading: true, error: "" }));
    try {
      const data = await addFollowUserShop(authToken, ownerId, storeShopId, !followState.isFollowing);
      if (String(storeStateRef.current?.data?.shopId || "") !== String(storeShopId || "")) return data;
      if (!data || data.error || data.success === false) {
        throw new Error(data?.error || "Impossible de modifier le suivi de cette boutique.");
      }

      const hasFollowList = Array.isArray(data.followShopUser);
      const nextFollowList = hasFollowList ? data.followShopUser : null;
      setFollowState((prev) => ({
        ...prev,
        ownerId,
        isFollowing: hasFollowList ? isShopFollowed(nextFollowList, storeShopId) : !prev.isFollowing,
        loading: false,
        error: "",
      }));
      if (nextFollowList && typeof infoSource?.setShopFollow === "function") {
        infoSource.setShopFollow(nextFollowList);
      }
      if (typeof infoSource?.readUser === "function") {
        await Promise.resolve(infoSource.readUser(authToken, ["getShopFollow"])).catch(() => null);
      }
      return data;
    } catch (error) {
      const message = error?.message || "Impossible de modifier le suivi de cette boutique.";
      setFollowState((prev) => ({ ...prev, ownerId, loading: false, error: message }));
      return { success: false, error: message };
    }
  }, [infoSource, storeOwnerId, storeShopId, followState.isFollowing, token]);

  const loadingUserPub = useCallback(() => Promise.resolve(null), []);

  const getCart = useCallback(async () => {
    const session = isAuthenticated() || {};
    const authToken = token || infoSource?.token || session?.token;
    if (!authToken) {
      if (typeof window !== "undefined") window.location.href = "/signin";
      return { error: "not-authenticated" };
    }

    const result = await getCartApi(authToken);
    if (!result?.error && typeof infoSource?.cartItems === "function") {
      await Promise.resolve(infoSource.cartItems(authToken)).catch(() => null);
    }
    return result;
  }, [infoSource, token]);

  const addToCart = useCallback(async (productArg = null, payload = {}) => {
    const current = storeStateRef.current;
    const product = productArg || resolvePath(current?.data, "products.0");
    if (!product?._id) return { error: "no-product" };

    const auth = isAuthenticated() || {};
    const authToken = token || infoSource?.token || auth.token;
    if (!authToken) {
      if (typeof window !== "undefined") window.location.href = "/signin";
      return { error: "not-authenticated" };
    }
    // ProductsPrice (shopPrice block) hands its card's [color, urlImg] pair.
    const cartPayload = Array.isArray(payload)
      ? { color: payload[0] || "", urlImg: payload[1] || "" }
      : payload && typeof payload === "object"
        ? payload
        : { filterProduct: payload || null };

    const firstCategory = Array.isArray(product.categoriesID) ? product.categoriesID[0] : null;

    // Same payload as Card.js (utils/cartItem.js): same variant, same image.
    const res = await addItem(authToken, { ...buildCartItemFromProduct(product, {
      ...cartPayload,
      categoryID: cartPayload.categoryID || firstCategory?.catID || firstCategory?._id || firstCategory || null,
    }), shopId: current?.data?.shopId || null });

    if (!res?.error && typeof infoSource?.cartItems === "function") {
      infoSource.cartItems(authToken);
    }
    return res;
  }, [infoSource, token]);

  const buyNow = useCallback(async (productArg = null, payload = {}) => {
    const result = await addToCart(productArg, payload);
    if (!result?.error) goToCheckout();
    return result;
  }, [addToCart, goToCheckout]);

  const storeActionOverrides = storeConfig?.actions;
  const storeActions = useMemo(() => ({
    loadProducts,
    loadindSearCh,
    loadProductsByArrival,
    loadProductsByArrival_,
    loadMore,
    loadMore0,
    appendShopProducts,
    onFilterShop,
    setFilterShop,
    setSearch,
    setPageShop,
    clearSearch,
    resetShopFilters,
    scrollToProducts,
    viewProduct,
    goToCart,
    goToCheckout,
    goToShop,
    clickToFollowShop,
    findToFollowU,
    unFollowShop,
    loadingUserPub,
    getCart,
    addToCart,
    buyNow,
    ...(storeActionOverrides || {}),
  }), [storeActionOverrides, addToCart, buyNow, clearSearch, clickToFollowShop, findToFollowU, getCart, goToCart, goToCheckout, goToShop, loadMore, loadMore0, appendShopProducts, loadProducts, loadingUserPub, loadProductsByArrival, loadProductsByArrival_, loadindSearCh, onFilterShop, resetShopFilters, scrollToProducts, setFilterShop, setPageShop, setSearch, unFollowShop, viewProduct]);

  useEffect(() => {
    if (typeof window === "undefined" || sourceKey !== "store") return undefined;
    window.__PB_STORE_ACTIONS = storeActions;
    return () => {
      if (window.__PB_STORE_ACTIONS === storeActions) delete window.__PB_STORE_ACTIONS;
    };
  }, [sourceKey, storeActions]);

  // Collecte des keyframes d'animations personnalisées
  const customKeyframes = (() => {
    const out = [];
    const walk = (n) => {
      if (n?.animation === "custom") out.push(buildCustomAnim(n).keyframe);
      n?.children?.forEach(walk);
    };
    (doc?.pages || []).forEach((p) => (p.children || []).forEach(walk));
    return out.join("\n");
  })();

  const handleNavigate = (target) => {
    if (!doc?.pages) return;
    const pg = doc.pages.find((p) => p.id === target || p.name === target);
    if (pg) {
      setActiveId(pg.id);
      if (onNavigate) onNavigate(pg.id);
      // Scroll vers le haut du conteneur (pas window pour éviter les conflits dans PageBuilder)
    } else if (onNavigate) {
      onNavigate(target);
    }
  };

  // A manually-edited HTML source (see PageHtmlSourceModal.jsx) overrides
  // ONE page's `doc` entry for RENDERING -- `doc` itself is never
  // touched/parsed, so restoring the generated HTML later always gets the
  // untouched builder tree back. Other pages of the SAME document, without
  // their own override, keep rendering normally below (nav between pages
  // stays fully functional either way, since `activeId` alone decides which
  // branch runs). See ManualHtmlPageFrame's own comment for why this is a
  // sandboxed iframe rather than dangerouslySetInnerHTML.
  //
  // `htmlSourcePages` (new, per-page map keyed by doc.pages[].id) takes
  // priority; `htmlSource` (legacy, single/global) is only ever consulted as
  // a fallback for the FIRST page -- exactly mirroring the server's own
  // readHtmlSourceForPage() so a document edited before the per-page
  // migration keeps rendering identically.
  const activePageHtmlSource =
    (htmlSourcePages && typeof htmlSourcePages === "object" && htmlSourcePages[activeId]) ||
    (doc?.pages?.[0]?.id === activeId ? htmlSource : null);
  if (activePageHtmlSource?.isManuallyEdited && typeof activePageHtmlSource.html === "string" && activePageHtmlSource.html.trim()) {
    return <ManualHtmlPageFrame html={activePageHtmlSource.html} pageData={pageData} sourceKey={sourceKey} />;
  }

  if (!doc || !doc.pages || !doc.pages.length) {
    // A Priority override built with ONLY Edit HTML Code (no JSON blocks at
    // all -- task spec §18/19: "Ne pas exiger pages[] pour custom HTML")
    // should never fall through to "No pages to display." just because
    // `activeId` couldn't be matched to a specific htmlSourcePages entry
    // above (e.g. no explicit pageId prop was passed). Any manually-edited
    // entry is a valid page to show here -- there is no ambiguity to
    // resolve when doc.pages is empty in the first place.
    const fallbackHtmlSource = (htmlSource?.isManuallyEdited && htmlSource.html?.trim())
      ? htmlSource
      : (htmlSourcePages && typeof htmlSourcePages === "object"
          ? Object.values(htmlSourcePages).find((entry) => entry?.isManuallyEdited && entry?.html?.trim())
          : null);
    if (fallbackHtmlSource) {
      return <ManualHtmlPageFrame html={fallbackHtmlSource.html} pageData={pageData} sourceKey={sourceKey} />;
    }
    return (
      <div style={{ padding: "60px", textAlign: "center", color: "#9b93b8", fontSize: "16px", fontFamily: "'DM Sans', system-ui, sans-serif" }}>{elakayAutoText("h_e47af401f9a9f6", "Aucune page à afficher.")}</div>
    );
  }

  const page = doc.pages.find((p) => p.id === activeId) || doc.pages[0];
  const pageStyle = page.style || { minHeight: "100vh", padding: "40px 20px", background: "#fff" };
  const runtimeData = sourceKey === "store" ? (storeState?.data || pageData) : pageData;
  const renderDevice = device === "desktop" ? "desktop" : "mobile";
  const layoutDevice = device === "tablet" ? "tablet" : renderDevice;
  const categoryReadyChildren = withRuntimeCategoryToggleGroups(page.children || [], renderDevice);
  const runtimePageChildren = sourceKey === "store" && !storeConfig?.disableFollowBar
    ? withRuntimeShopFollowButton(categoryReadyChildren, doc?.meta?.templateKey)
    : categoryReadyChildren;
  const toggleGroupStyles = collectToggleGroupStyles(
    runtimePageChildren,
    renderDevice
  );

  const isShopTemplate = doc.pageMode === "store" && Boolean(doc?.meta?.templateKey);
  const isCompactDevice = device === "mobile" || device === "tablet";
  const fullWidthShopTemplate = isShopTemplate && isCompactDevice;

  // Les SHOP_TEMPLATES remplissent tout l'écran mobile/tablette.
  const deviceMaxW = fullWidthShopTemplate
    ? "none"
    : device === "mobile"
      ? "390px"
      : device === "tablet"
        ? "768px"
        : "none";
  const deviceMargin = fullWidthShopTemplate ? 0 : deviceMaxW !== "none" ? "0 auto" : undefined;
  const freeLayout = getPageLayoutMode(page, layoutDevice) === "free";
  const renderPageChild = (child) => {
    const rendered = <RenderNode node={child} onNavigate={handleNavigate} />;
    if (!freeLayout) return <React.Fragment key={child.id}>{rendered}</React.Fragment>;
    const position = getFreeLayout(child, layoutDevice);
    return (
      <div key={child.id} style={{ position: "absolute", left: `${position.x}${position.unit}`, top: `${position.y}${position.unit}`, zIndex: position.z }}>
        {rendered}
      </div>
    );
  };

  return (
    <DeviceContext.Provider value={device === "desktop" ? "desktop" : "mobile"}>
      <DataContext.Provider value={{ data: runtimeData, sourceKey, onSign, infoSource, token, storeActions, storeState, followState, toggleGroupStyles, activeToggles, setActiveToggle, storeConfig }}>
        {/* Keyframes globales */}
        <style>{ANIMATION_KEYFRAMES}</style>
        {customKeyframes && <style>{customKeyframes}</style>}

        {/* Navigation multi-pages visible en bas (si doc a plusieurs pages) */}
        {doc.pages.length > 1 && !storeConfig?.hidePageNavigation && (
          <div style={{ display: "flex", gap: "6px", padding: "10px 16px", background: "#faf9fd", borderBottom: "1px solid #ece9f5", flexWrap: "wrap" }}>
            {doc.pages.map((p) => (
              <button
                key={p.id}
                onClick={() => handleNavigate(p.id)}
                style={{ padding: "5px 12px", fontSize: "12px", fontWeight: p.id === activeId ? 700 : 500, border: `1px solid ${p.id === activeId ? "#6d28d9" : "#ece9f5"}`, borderRadius: "8px", cursor: "pointer", background: p.id === activeId ? "#f5f3ff" : "#fff", color: p.id === activeId ? "#6d28d9" : "#7a73a0" }}
              >
                {p.name}
              </button>
            ))}
          </div>
        )}

        {/* Contenu de la page */}
        <div ref={pageRootRef} style={{ ...pageStyle, width: "100%", fontFamily: "'DM Sans', system-ui, sans-serif", color: "#1a1a2e", maxWidth: deviceMaxW, margin: deviceMargin, ...(freeLayout ? { position: "relative" } : {}) }}>
          {runtimePageChildren.map(renderPageChild)}
        </div>
      </DataContext.Provider>
    </DeviceContext.Provider>
  );
}

// =============================================================================
//  PageRendererConnected — composant CONNECTÉ (fait les appels API)
//  Charge le document sauvegardé du PageBuilder ET les données du pageMode
//  depuis vos API, gère les états chargement/erreur, puis délègue au
//  composant pur <PageRenderer />.
//
//  Utilisation simple (par URLs) :
//     <PageRendererConnected
//        docUrl={`/api/pagebuilder/${pageId}`}
//        dataUrl={`/api/shop/${shopId}`}   // optionnel selon le pageMode
//     />
//
//  Utilisation avancée (fonctions de fetch personnalisées) :
//     <PageRendererConnected
//        fetchDoc={() => api.getPage(pageId)}              // -> renvoie { doc, pageMode } ou doc
//        fetchData={(pageMode) => api.getDataFor(pageMode)} // -> renvoie les données du mode
//     />
//
//  Props :
//    - docUrl     : URL de l'API qui renvoie le document sauvegardé (doc + pageMode)
//    - dataUrl    : URL de l'API qui renvoie les données du pageMode (optionnel)
//    - fetchDoc   : fonction async alternative pour charger le doc (prioritaire sur docUrl)
//    - fetchData  : fonction async alternative pour charger les données (reçoit le pageMode)
//    - device, pageId, onNavigate : transmis tels quels au composant pur
//    - headers    : en-têtes HTTP optionnels (ex. { Authorization: `Bearer ${token}` })
//    - loadingNode/errorNode : éléments React optionnels pour personnaliser ces états
// =============================================================================
export function PageRendererConnected({
  docUrl,
  dataUrl,
  fetchDoc,
  fetchData,
  device = "desktop",
  pageId,
  onNavigate,
  onSign,
  headers = {},
  loadingNode,
  errorNode,
}) {
  const [state, setState] = useState({ loading: true, error: null, doc: null, pageData: null });

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setState((s) => ({ ...s, loading: true, error: null }));
      try {
        // 1) Charger le document sauvegardé du PageBuilder
        let docResult;
        if (fetchDoc) {
          docResult = await fetchDoc();
        } else if (docUrl) {
          const res = await fetch(docUrl, { headers });
          if (!res.ok) throw new Error(`Échec du chargement du document (HTTP ${res.status})`);
          docResult = await res.json();
        } else {
          throw new Error("Aucune source de document fournie (docUrl ou fetchDoc requis).");
        }
        // Le doc peut être { doc, pageMode } (enveloppe du schéma) ou directement le doc
        const doc = docResult?.doc || docResult;
        const pageMode = docResult?.pageMode || doc?.pageMode || doc?.meta?.pageMode || "static";

        // 2) Charger les données du pageMode (sauf en mode static qui n'en a pas besoin)
        let pageData = null;
        if (pageMode !== "static") {
          if (fetchData) {
            pageData = await fetchData(pageMode);
          } else if (dataUrl) {
            const res = await fetch(dataUrl, { headers });
            if (!res.ok) throw new Error(`Échec du chargement des données (HTTP ${res.status})`);
            pageData = await res.json();
          }
          // si aucune source de données n'est fournie, on rend quand même (sans bindings actifs)
        }

        if (!cancelled) setState({ loading: false, error: null, doc: { ...doc, pageMode }, pageData });
      } catch (err) {
        if (!cancelled) setState({ loading: false, error: err.message || "Erreur de chargement", doc: null, pageData: null });
      }
    };
    run();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docUrl, dataUrl, pageId]);

  if (state.loading) {
    return loadingNode || (
      <div style={{ padding: "60px", textAlign: "center", color: "#9b93b8", fontSize: "15px", fontFamily: "'DM Sans', system-ui, sans-serif" }}>
        <div style={{ width: "40px", height: "40px", margin: "0 auto 14px", border: "4px solid #ece9f5", borderTopColor: "#6d28d9", borderRadius: "50%", animation: "pb-spin 0.8s linear infinite" }} />
        <style>{`@keyframes pb-spin{to{transform:rotate(360deg)}}`}</style>{elakayAutoText("h_fe121184d9e255", "Chargement de la page…")}</div>
    );
  }
  if (state.error) {
    return errorNode || (
      <div style={{ padding: "60px", textAlign: "center", color: "#e11d48", fontSize: "15px", fontFamily: "'DM Sans', system-ui, sans-serif" }}>{elakayAutoText("h_9cf4c826b005db", "Impossible de charger la page :")}{state.error}
      </div>
    );
  }
  return <PageRenderer doc={state.doc} pageData={state.pageData} device={device} pageId={pageId} onNavigate={onNavigate} onSign={onSign} />;
}
